'use client'

import * as React from 'react'
import { useState, useMemo, useEffect } from 'react'
import { useAuth } from '../contexts/auth-context'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { expenseApi, chartOfAccountsApi, expenseJournalApi, purchaseApi } from '../lib/api/accounting'
import { apiService } from '../lib/api'
import { bankingApi } from '../lib/api/banking'
import { getCompanyId, getTenantId, getApiUrl } from '../lib/config'
import { VendorModal } from '../components/vendor-modal'
import { formatCurrency } from '../lib/utils'
import { PageLayout } from '../components/page-layout'
import { TranslatedText } from '../components/translated-text'
import { useTranslation, useTranslatedText } from '../hooks/useTranslation'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog'
import { SegmentedTabs } from '../components/ui/segmented-tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form'
import { Textarea } from '../components/ui/textarea'
import { Progress } from '../components/ui/progress'
import { Separator } from '../components/ui/separator'
import { MoonLoader } from '../components/ui/moon-loader'
import { ReceiptCaptureModal } from '../components/receipt-capture'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu'
import { ExpenseReportModal } from '../components/expense-report-modal'
import { ReimburseExpenseModal } from '../components/reimburse-expense-modal'
import { ExpenseMatchingModal } from '../components/expense-matching-modal'
import { EditExpenseModal } from '../components/edit-expense-modal'
import { ExpenseFormFields, ExpenseSummary } from '../components/expense-form'
import { CardCsvImportModal } from '../components/card-csv-import'
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  X, 
  Trash2, 
  FolderTree,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Calendar,
  BarChart3,
  PieChart,
  Target,
  Settings,
  ChevronRight,
  ChevronDown,
  FileText,
  Building,
  Upload,
  Calculator,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  Download,
  RefreshCw,
  Check,
  FileSpreadsheet,
  Printer,
  Archive,
  BookOpen,
  Receipt,
  CreditCard,
  Banknote,
  Tag
} from 'lucide-react'
import { format } from 'date-fns'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
// Local page types (keeps shape independent from shared API types)
// Types
interface ExpenseCategory {
  id: string
  name: string
  description?: string
  parentId?: string
  color?: string
  icon?: string
  isActive: boolean
  taxTreatment?: 'deductible' | 'non-deductible' | 'partially_deductible'
  approvalThreshold?: number
  children: ExpenseCategory[]
  budgets: Budget[]
  createdAt: string
  updatedAt: string
}

interface Budget {
  id: string
  name: string
  description?: string
  period: 'monthly' | 'quarterly' | 'yearly'
  startDate: string
  endDate: string
  amount: number
  spentAmount: number
  isActive: boolean
  alertThreshold?: number
  category: { id: string; name: string }
  companyId?: string
  createdAt: string
  updatedAt: string
}

interface ExpenseRule {
  id: string
  name: string
  description?: string
  ruleType: 'amount_limit' | 'vendor_restriction' | 'approval_required'
  conditions: string
  actions: string
  isActive: boolean
  priority: number
  category: { id: string; name: string }
  createdAt: string
  updatedAt: string
}

interface Company {
  id: string
  name: string
}

// Helper function to get all child category IDs recursively
const getAllChildCategoryIds = (category: ExpenseCategory, allCategories: ExpenseCategory[]): string[] => {
  const childIds: string[] = []
  if (category.children && category.children.length > 0) {
    category.children.forEach(child => {
      childIds.push(child.id)
      // Recursively get children of children
      const nestedChild = allCategories.find(c => c.id === child.id)
      if (nestedChild) {
        childIds.push(...getAllChildCategoryIds(nestedChild, allCategories))
      }
    })
  }
  return childIds
}

// Validation schemas
const categorySchema = z.object({
  companyId: z.string().min(1, 'Company is required'),
  name: z.string().min(1, 'Category name is required'),
  description: z.string().optional(),
  parentId: z.string().refine((val) => {
    // Allow empty string, "none", or valid UUID
    if (!val || val === '' || val === 'none') return true
    // Basic UUID validation
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(val)
  }, {
    message: 'Parent category must be a valid category ID'
  }).optional(),
  color: z.string().optional(),
  icon: z.string().optional(),
  taxTreatment: z.enum(['deductible', 'non-deductible', 'partially_deductible']).optional(),
  approvalThreshold: z.coerce.number().positive().optional()
})

const budgetSchema = z.object({
  companyId: z.string().min(1, 'Company is required'),
  categoryId: z.string().min(1, 'Category is required'),
  name: z.string().min(1, 'Budget name is required'),
  description: z.string().optional(),
  period: z.enum(['monthly', 'quarterly', 'yearly']),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  amount: z.coerce.number().positive('Amount must be positive'),
  alertThreshold: z.coerce.number().optional()
})

const ruleSchema = z.object({
  companyId: z.string().min(1, 'Company is required'),
  categoryId: z.string().min(1, 'Category is required'),
  name: z.string().min(1, 'Rule name is required'),
  description: z.string().optional(),
  ruleType: z.enum(['amount_limit', 'vendor_restriction', 'approval_required']),
  // User-friendly condition fields
  amountLimit: z.coerce.number().positive().optional(),
  blockedVendors: z.string().optional(),
  requireApproval: z.boolean().optional(),
  notifyManager: z.boolean().optional(),
  autoReject: z.boolean().optional(),
  // Legacy JSON fields (hidden, auto-generated)
  conditions: z.string().optional(),
  actions: z.string().optional(),
  priority: z.coerce.number().int().min(1, 'Priority must be at least 1')
})

// Journal Entry Indicator Component
const ExpenseJournalIndicator = ({ expenseId }: { expenseId: string }) => {
  const { data: journalEntries, isLoading } = useQuery({
    queryKey: ['expense-journal-entries', expenseId],
    queryFn: () => expenseJournalApi.getJournalEntries(expenseId),
    enabled: !!expenseId
  })

  if (isLoading) {
    return (
      <div className="w-4 h-4 animate-pulse bg-gray-200 rounded"></div>
    )
  }

  const hasEntries = journalEntries && journalEntries.length > 0

  return (
    <div 
      className={`w-2 h-2 rounded-full ${
        hasEntries ? 'bg-green-500' : 'bg-gray-300'
      }`}
      title={hasEntries ? 'Journal entries exist' : 'No journal entries'}
    />
  )
}

// Journal Entries Component
const ExpenseJournalEntries = ({ expenseId }: { expenseId: string }) => {
  const { data: journalEntries, isLoading, error } = useQuery({
    queryKey: ['expense-journal-entries', expenseId],
    queryFn: () => expenseJournalApi.getJournalEntries(expenseId),
    enabled: !!expenseId
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <MoonLoader size="sm" color="teal" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-4 text-red-600">
        <p>Error loading journal entries</p>
      </div>
    )
  }

  if (!journalEntries || journalEntries.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No journal entries found for this expense</p>
        <p className="text-sm">Journal entries are created automatically when expenses are approved</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {journalEntries.map((entry, index) => (
        <Card key={entry.id} className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-blue-600" />
                <CardTitle className="text-sm font-medium">
                  Journal Entry #{index + 1}
                </CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={entry.status === 'POSTED' ? 'default' : 'secondary'}>
                  {entry.status}
                </Badge>
                <span className="text-xs text-gray-500">
                  {format(new Date(entry.date), 'MMM dd, yyyy')}
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-600">{entry.memo}</p>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {entry.lines?.map((line, lineIndex) => (
                <div key={lineIndex} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {line.debit > 0 ? (
                        <div className="flex items-center gap-1 text-red-600">
                          <ArrowUp className="h-3 w-3" />
                          <span className="text-xs font-medium">DR</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-green-600">
                          <ArrowDown className="h-3 w-3" />
                          <span className="text-xs font-medium">CR</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{line.account?.name}</p>
                      <p className="text-xs text-gray-500">{line.account?.code}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      ${(line.debit || line.credit || 0).toLocaleString()}
                    </p>
                    {line.memo && (
                      <p className="text-xs text-gray-500">{line.memo}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {entry.createdBy && (
              <div className="mt-3 pt-2 border-t text-xs text-gray-500">
                Created by {entry.createdBy.name || entry.createdBy.email} on{' '}
                {format(new Date(entry.createdAt), 'MMM dd, yyyy HH:mm')}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export default function ExpensesPage() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth()
  const { tSync } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState('categories')
  
  // Use useTranslatedText for reactive tab labels and search placeholders
  const categoriesLabel = useTranslatedText('Categories')
  const budgetsLabel = useTranslatedText('Budgets')
  const rulesLabel = useTranslatedText('Rules')
  const expensesLabel = useTranslatedText('Expenses')
  const searchPlaceholder = useTranslatedText('Search expenses, categories, budgets...')
  const searchBudgetsPlaceholder = useTranslatedText('Search budgets...')
  const searchRulesPlaceholder = useTranslatedText('Search rules...')
  const searchExpensesPlaceholder = useTranslatedText('Search expenses...')
  const [expenseStatus, setExpenseStatus] = useState<string>('all')
  const [expenseCategoryId, setExpenseCategoryId] = useState<string>('')
  const [expenseStartDate, setExpenseStartDate] = useState<string>('')
  const [expenseEndDate, setExpenseEndDate] = useState<string>('')
  const [expenseDepartment, setExpenseDepartment] = useState<string>('')
  const [expenseProject, setExpenseProject] = useState<string>('')
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false)
  const [isCreateBudgetOpen, setIsCreateBudgetOpen] = useState(false)
  const [isViewBudgetOpen, setIsViewBudgetOpen] = useState(false)
  const [isCreateRuleOpen, setIsCreateRuleOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<ExpenseRule | null>(null)
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null)
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | null>(null)
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [receiptOpen, setReceiptOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [reimburseOpen, setReimburseOpen] = useState(false)
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(null)
  const [selectedExpense, setSelectedExpense] = useState<any | null>(null)
  const [isViewExpenseOpen, setIsViewExpenseOpen] = useState(false)
  const [isCreateExpenseOpen, setIsCreateExpenseOpen] = useState(false)
  const [matchingOpen, setMatchingOpen] = useState(false)
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false)
  const [matchingContext, setMatchingContext] = useState<{ amount?: number; date?: string; description?: string } | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editingExpense, setEditingExpense] = useState<any | null>(null)
  const [cardImportOpen, setCardImportOpen] = useState(false)
  
  // Sorting and pagination state
  const [sortField, setSortField] = useState<string>('expenseDate')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedExpenses, setSelectedExpenses] = useState<Set<string>>(new Set())
  const [expenseJournalEntries, setExpenseJournalEntries] = useState<Map<string, any[]>>(new Map())
  const [submittingExpenseId, setSubmittingExpenseId] = useState<string | null>(null)
  const [approvingExpenseId, setApprovingExpenseId] = useState<string | null>(null)
  const [bulkApproving, setBulkApproving] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [expenseToDelete, setExpenseToDelete] = useState<any | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false)
  
  // OCR State
  const [ocrFile, setOcrFile] = useState<File | null>(null)
  const [ocrPreviewUrl, setOcrPreviewUrl] = useState<string | null>(null)
  const [ocrExtracting, setOcrExtracting] = useState(false)
  const [ocrResultsOpen, setOcrResultsOpen] = useState(false)
  const [ocrExtractedData, setOcrExtractedData] = useState<{ 
    vendor?: string; 
    amount?: number; 
    date?: string; 
    description?: string; 
    rawText?: string;
    items?: Array<{ 
      name: string; 
      amount: number; 
      quantity?: number; 
      unitPrice?: number; 
      itemCode?: string;
    }>;
    currency?: string;
  } | null>(null)
  const [bulkExpenseModalOpen, setBulkExpenseModalOpen] = useState(false)
  const [editableExpenses, setEditableExpenses] = useState<Array<{
    description: string;
    amount: number;
    categoryId: string;
    vendorId: string;
    vendorName: string;
    date: string;
    notes: string;
    currency: string;
    taxRate: number;
    taxAmount: number;
    totalAmount: number;
    department: string;
    project: string;
    paymentMethod: string;
    referenceNumber: string;
    accountId: string;
  }>>([])
  const [currentExpenseIndex, setCurrentExpenseIndex] = useState(0)
  const [bulkTaxAmount, setBulkTaxAmount] = useState(0)
  const [validationErrors, setValidationErrors] = useState<Record<number, {
    description?: boolean;
    categoryId?: boolean;
    amount?: boolean;
    date?: boolean;
  }>>({})
  
  // Currency Conversion State
  const [convertedBudgeted, setConvertedBudgeted] = useState<number | null>(null)
  const [convertedSpent, setConvertedSpent] = useState<number | null>(null)
  const [convertingCurrency, setConvertingCurrency] = useState(false)
  const [selectedExpensesTotalRWF, setSelectedExpensesTotalRWF] = useState<number | null>(null)
  const [convertingSelectedTotal, setConvertingSelectedTotal] = useState(false)
  
  const queryClient = useQueryClient()

  // Company ID is managed by header component and stored in localStorage

  // Get company ID directly from localStorage (header ensures it's seed-company-1)
  const firstCompanyId = useMemo(() => {
    return getCompanyId();
  }, [])

  // Fetch company data to get the company name
  const { data: companyData, isLoading: companyLoading, error: companyError } = useQuery({
    queryKey: ['company', firstCompanyId],
    queryFn: async () => {
      return await apiService.getCompany(firstCompanyId);
    },
    enabled: !!firstCompanyId,
    retry: 2,
    retryDelay: 1000
  })

  // Get company name for display
  const companyName = companyData?.name || (companyLoading ? 'Loading...' : 'Company')

  // Fetch expense categories (uses company ID from localStorage)
  const { data: categories, isLoading: categoriesLoading } = useQuery<any>({
    queryKey: ['expense-categories', searchTerm, firstCompanyId],
    queryFn: async () => {
      const result = await expenseApi.getExpenseCategories({ companyId: firstCompanyId, q: searchTerm || undefined }) as any;
      // Handle different response formats
      if (Array.isArray(result)) return result;
      if (result?.data && Array.isArray(result.data)) return result.data;
      if (result?.items && Array.isArray(result.items)) return result.items;
      return [];
    },
    enabled: isAuthenticated && !authLoading && !!firstCompanyId
  })

  const { data: budgets, isLoading: budgetsLoading } = useQuery<any>({
    queryKey: ['budgets', firstCompanyId],
    queryFn: async () => {
      const result = await expenseApi.getBudgets() as any;
      // Handle different response formats
      if (Array.isArray(result)) return result;
      if (result?.data && Array.isArray(result.data)) return result.data;
      if (result?.items && Array.isArray(result.items)) return result.items;
      return [];
    },
    enabled: isAuthenticated && !authLoading && !!firstCompanyId
  })

  const { data: rules, isLoading: rulesLoading } = useQuery<any>({
    queryKey: ['expense-rules', firstCompanyId],
    queryFn: async () => await expenseApi.getExpenseRules() as any,
    enabled: isAuthenticated && !authLoading && !!firstCompanyId
  })

  const { data: analytics, isLoading: analyticsLoading } = useQuery<any>({
    queryKey: ['budget-analytics'],
    queryFn: async () => await expenseApi.getBudgetAnalytics() as any
  })

  // Fetch GL accounts for expense form
  const { data: accountsData } = useQuery<any>({
    queryKey: ['gl-accounts', firstCompanyId],
    queryFn: async () => {
      const result = await chartOfAccountsApi.getAll(firstCompanyId) as any;
      return result;
    },
    enabled: isAuthenticated && !authLoading && !!firstCompanyId
  })

  // Fetch vendors for expense form
  const { data: vendors } = useQuery<any>({
    queryKey: ['vendors', firstCompanyId],
    queryFn: async () => {
      const result = await purchaseApi.getVendors(firstCompanyId) as any;
      // Handle different response formats
      if (Array.isArray(result)) return result;
      if (result?.data && Array.isArray(result.data)) return result.data;
      if (result?.items && Array.isArray(result.items)) return result.items;
      return [];
    },
    enabled: isAuthenticated && !authLoading && !!firstCompanyId
  })

  // Expenses list (loaded when expenses tab is active)
  const { data: expenses, isLoading: expensesLoading } = useQuery<any>({
    queryKey: ['expenses', expenseStatus, expenseCategoryId],
    enabled: activeTab === 'expenses',
    queryFn: async () => await expenseApi.getExpenses(undefined, expenseStatus === 'all' ? undefined : expenseStatus, expenseCategoryId || undefined) as any
  })

  // Fetch individual expense details
  const { data: expenseDetails, isLoading: expenseDetailsLoading } = useQuery<any>({
    queryKey: ['expense', selectedExpense?.id],
    enabled: !!selectedExpense?.id,
    queryFn: async () => await expenseApi.getExpenseById(selectedExpense.id) as any
  })

  const submitExpense = useMutation({
    mutationFn: async (id: string) => {
      setSubmittingExpenseId(id)
      return await expenseApi.submitExpense(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      toast.success('Expense submitted')
      setSubmittingExpenseId(null)
    },
    onError: () => {
      setSubmittingExpenseId(null)
    }
  })

  // Create vendor mutation
  const createVendorMutation = useMutation({
    mutationFn: async (vendorData: any) => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token')
      const tenantId = getTenantId()
      const response = await fetch(
        getApiUrl('api/enhanced-transaction-processing/vendors'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': firstCompanyId
          },
          body: JSON.stringify(vendorData)
        }
      )
      const data = await response.json()
      if (!data.success) {
        // Handle duplicate vendor error specifically
        if (data.error === 'Vendor already exists' || data.message?.includes('already exists')) {
          throw new Error(data.message || 'A vendor with this name already exists for this company.')
        }
        throw new Error(data.message || data.error || 'Failed to create vendor')
      }
      return data.data
    },
    onSuccess: (newVendor) => {
      // Invalidate vendors query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['vendors', firstCompanyId] })
      setIsVendorModalOpen(false)
      toast.success('Vendor created successfully')
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to create vendor'
      toast.error(errorMessage)
    }
  })

  const approveExpense = useMutation({
    mutationFn: async ({ id, silent = false }: { id: string; silent?: boolean }) => {
      if (!silent) {
        setApprovingExpenseId(id)
      }
      try {
        return await expenseApi.approveExpense(id);
      } catch (error: any) {
        // Extract error message from the error response
        const errorMessage = error?.response?.data?.details || 
                           error?.response?.data?.error?.message || 
                           error?.message || 
                           'Failed to approve expense';
        throw new Error(errorMessage);
      }
    },
    onSuccess: async (data, variables) => {
      const { id, silent = false } = variables;
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      
      if (!silent) {
      toast.success('Expense approved');
        setApprovingExpenseId(null)
      }
      
      // Automatically trigger accounting after approval (silently for bulk operations)
      try {
        const hasEntries = await hasJournalEntries(id);
        if (!hasEntries) {
          // Generate journal entry automatically after approval
          if (!silent) {
            generateJournalEntry.mutate({ expenseId: id, silent: false });
        } else {
            // For bulk operations, generate journal entry silently
            try {
              await generateJournalEntry.mutateAsync({ expenseId: id, silent: true });
            } catch (error) {
              // Silently fail journal entry generation during bulk operations
            }
          }
        }
        // Removed "journal entries already exist" notification
      } catch (error) {
        // Don't show error toast for this, as the main approval was successful
      }
    },
    onError: (error: Error, variables) => {
      const { silent = false } = variables;
      if (!silent) {
      toast.error(error.message || 'Failed to approve expense');
        setApprovingExpenseId(null)
      }
    }
  })

  const markPaid = useMutation({
    mutationFn: async (id: string) => expenseApi.updateExpense(id, { status: 'paid' } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      toast.success('Expense marked as paid')
    }
  })

  // Simple client-side policy enforcement using expense rules
  const checkPolicies = async (expense: any): Promise<{ ok: boolean; message?: string }> => {
    try {
      const rules = await expenseApi.getExpenseRules()
      for (const r of (rules || [])) {
        const type = r.ruleType || r.type || ''
        let conditions: any = {}
        try { conditions = r.conditions ? JSON.parse(r.conditions) : {} } catch {}
        if (type === 'amount_limit') {
          const limit = Number(conditions.amount || conditions.limit || 0)
          if (limit && Number(expense.totalAmount ?? expense.amount ?? 0) > limit) {
            return { ok: false, message: `Amount exceeds policy limit of ${limit}` }
          }
        }
        if (type === 'vendor_restriction') {
          const blocked = (conditions.vendors || conditions.blocked || []) as string[]
          const name = expense.vendor?.name || expense.vendorName || ''
          if (Array.isArray(blocked) && blocked.some(v => v && name && name.toLowerCase().includes(String(v).toLowerCase()))) {
            return { ok: false, message: `Vendor restricted by policy` }
          }
        }
        if (type === 'approval_required') {
          // If a rule explicitly requires approval, block direct submit to paid/approved paths
          // Here we allow submit (moves to submitted) but prevent direct approve if not yet reviewed
          // Enforcement handled in handlers
        }
      }
    } catch {}
    return { ok: true }
  }

  const handleSubmitExpense = async (e: any) => {
    const res = await checkPolicies(e)
    if (!res.ok) { toast.error(res.message || 'Policy violation'); return }
    submitExpense.mutate(e.id)
  }

  const handleApproveExpense = async (e: any) => {
    try {
      const rules = await expenseApi.getExpenseRules()
      const requiresApproval = (rules || []).some((r: any) => (r.ruleType || r.type) === 'approval_required')
      const role = (user as any)?.role || 'employee'
      const isApprover = role === 'admin' || role === 'accountant'
      if (requiresApproval && !isApprover) {
        toast.error('Approval requires an approver role')
        return
      }
    } catch {}
    approveExpense.mutate({ id: e.id, silent: false })
  }

  // Export and bulk operations functions
  const exportToCSV = (expensesToExport: any[]) => {
    if (expensesToExport.length === 0) {
      toast.error('No expenses to export')
      return
    }

    const headers = ['Date', 'Supplier', 'Item Name', 'Item category', 'Amount', 'Payment mode (cash/Bank)']
    const rows = expensesToExport.map(e => [
      e.expenseDate ? format(new Date(e.expenseDate), 'yyyy-MM-dd') : '',
      String((e as any).vendorName || '').trim(),
      String(e.description || '').trim(),
      String(e.category?.name || '').trim(),
      Number(e.totalAmount || e.amount || 0).toFixed(2),
      String((e as any).paymentMethod || '-').trim()
    ])

    // Helper function to properly escape CSV cells
    const escapeCSVCell = (value: any): string => {
      // Handle null, undefined, or empty values
      if (value === null || value === undefined || value === '') {
        return ''
      }
      
      // Convert to string and trim
      let str = String(value).trim()
      
      // If empty after trim, return empty string (no quotes)
      if (str === '' || str === 'null' || str === 'undefined') {
        return ''
      }
      
      // Remove any surrounding quotes that might already be there
      // Handle cases like '"', '""', '"""', etc.
      while (str.startsWith('"') && str.endsWith('"') && str.length > 1) {
        str = str.slice(1, -1).replace(/""/g, '"')
      }
      
      // If after removing quotes it's empty or just a quote, return empty
      if (str === '' || str === '"') {
        return ''
      }
      
      // Only wrap in quotes if cell contains comma, quote, or newline
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`
      }
      
      return str
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(escapeCSVCell).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `expenses_${format(new Date(), 'yyyy-MM-dd_HHmmss')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast.success(`Exported ${expensesToExport.length} expense(s) to CSV`)
  }

  const exportToExcel = (expensesToExport: any[]) => {
    if (expensesToExport.length === 0) {
      toast.error('No expenses to export')
      return
    }

    // Create HTML table for Excel
    const headers = ['Date', 'Supplier', 'Item Name', 'Item category', 'Amount', 'Payment mode (cash/Bank)']
    const rows = expensesToExport.map(e => [
      e.expenseDate ? format(new Date(e.expenseDate), 'yyyy-MM-dd') : '',
      (e as any).vendorName || '',
      e.description || '',
      e.category?.name || '',
      Number(e.totalAmount || e.amount || 0).toFixed(2),
      (e as any).paymentMethod || '-'
    ])

    const htmlTable = `
      <html xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head>
          <meta charset="UTF-8">
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>Expenses</x:Name>
                  <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
        </head>
        <body>
          <table border="1">
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `

    const blob = new Blob([htmlTable], { type: 'application/vnd.ms-excel' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `expenses_${format(new Date(), 'yyyy-MM-dd_HHmmss')}.xls`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast.success(`Exported ${expensesToExport.length} expense(s) to Excel`)
  }

  const printExpenses = (expensesToPrint: any[]) => {
    if (expensesToPrint.length === 0) {
      toast.error('No expenses to print')
      return
    }

    const printWindow = window.open('', '', 'height=800,width=1000')
    if (!printWindow) {
      toast.error('Please allow popups to print')
      return
    }

    // Use the same fields as CSV export: Date, Supplier, Item Name, Item category, Amount, Payment mode (cash/Bank)
    const headers = ['Date', 'Supplier', 'Item Name', 'Item category', 'Amount', 'Payment mode (cash/Bank)']
    const rows = expensesToPrint.map(e => ({
      date: e.expenseDate ? format(new Date(e.expenseDate), 'yyyy-MM-dd') : '',
      supplier: String((e as any).vendorName || '').trim(),
      itemName: String(e.description || '').trim(),
      itemCategory: String(e.category?.name || '').trim(),
      amount: Number(e.totalAmount || e.amount || 0).toFixed(2),
      paymentMode: String((e as any).paymentMethod || '-').trim()
    }))

    const totalAmount = expensesToPrint.reduce((sum, e) => sum + Number(e.totalAmount || e.amount || 0), 0)

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Expenses Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #333; border-bottom: 2px solid #10b981; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: #10b981; color: white; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .total { font-weight: bold; background-color: #e0f2f1; }
            .header-info { margin-bottom: 20px; }
            @media print {
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header-info">
            <h1>Expenses Report</h1>
            <p><strong>Generated:</strong> ${format(new Date(), 'MMMM dd, yyyy - hh:mm a')}</p>
            <p><strong>Total Expenses:</strong> ${expensesToPrint.length}</p>
            <p><strong>Total Amount:</strong> ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Supplier</th>
                <th>Item Name</th>
                <th>Item category</th>
                <th>Amount</th>
                <th>Payment mode (cash/Bank)</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map(r => `
                <tr>
                  <td>${r.date || '-'}</td>
                  <td>${r.supplier || '-'}</td>
                  <td>${r.itemName || '-'}</td>
                  <td>${r.itemCategory || '-'}</td>
                  <td>${r.amount}</td>
                  <td>${r.paymentMode || '-'}</td>
                </tr>
              `).join('')}
              <tr class="total">
                <td colspan="4" style="text-align: right;"><strong>TOTAL:</strong></td>
                <td colspan="2"><strong>${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw</strong></td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `

    printWindow.document.write(htmlContent)
    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
    }, 250)

    toast.success(`Printing ${expensesToPrint.length} expense(s)`)
  }

  const handleBulkApprove = async () => {
    if (selectedExpenses.size === 0) {
      toast.error('No expenses selected')
      return
    }

    try {
      const rules = await expenseApi.getExpenseRules()
      const requiresApproval = (rules || []).some((r: any) => (r.ruleType || r.type) === 'approval_required')
      const role = (user as any)?.role || 'employee'
      const isApprover = role === 'admin' || role === 'accountant'
      if (requiresApproval && !isApprover) {
        toast.error('Bulk approval requires an approver role')
        return
      }

      // Filter only unapproved expenses from selected expenses
      const expenseIds = Array.from(selectedExpenses)
      const expensesToApprove = ((Array.isArray(expenses) ? expenses : expenses?.items || expenses?.data || []) as any[])
        .filter((e: any) => expenseIds.includes(e.id) && e.status !== 'approved' && e.status !== 'paid')
      
      if (expensesToApprove.length === 0) {
        toast.warning('No unapproved expenses selected')
        return
      }

      setBulkApproving(true)

      try {
        // Use optimized bulk approve endpoint
        const expenseIds = expensesToApprove.map(e => e.id)
        const result = await expenseApi.bulkApproveExpenses(expenseIds)
        
        setSelectedExpenses(new Set())
        queryClient.invalidateQueries({ queryKey: ['expenses'] })

        // Show summary notification
        if (result.results.failed === 0) {
          toast.success(`Successfully approved ${result.results.successful} expense(s)`)
        } else {
          toast.warning(`Approved ${result.results.successful} expense(s), ${result.results.failed} failed`)
          if (result.results.errors.length > 0) {
            // Show first few errors in console for debugging
          }
        }
      } catch (bulkError: any) {
        // Fallback to individual approvals if bulk endpoint fails
        
        // Process all approvals in parallel for better performance (silently to avoid individual notifications)
        const approvalPromises = expensesToApprove.map(expense => 
          approveExpense.mutateAsync({ id: expense.id, silent: true })
        )

        const results = await Promise.allSettled(approvalPromises)
        
        const successCount = results.filter(r => r.status === 'fulfilled').length
        const errorCount = results.filter(r => r.status === 'rejected').length

        setSelectedExpenses(new Set())
        queryClient.invalidateQueries({ queryKey: ['expenses'] })

        // Show single summary notification
        if (errorCount === 0) {
          toast.success(`Successfully approved ${successCount} expense(s)`)
        } else {
          toast.warning(`Approved ${successCount} expense(s), ${errorCount} failed`)
        }
      }
    } catch (error) {
      toast.error('Failed to bulk approve expenses')
    } finally {
      setBulkApproving(false)
    }
  }

  const handleBulkReject = async () => {
    if (selectedExpenses.size === 0) {
      toast.error('No expenses selected')
      return
    }

    const expenseIds = Array.from(selectedExpenses)
    let successCount = 0
    let errorCount = 0

    for (const id of expenseIds) {
      try {
        await updateExpense.mutateAsync({ id, data: { status: 'rejected' } })
        successCount++
      } catch (error) {
        errorCount++
      }
    }

    setSelectedExpenses(new Set())
    queryClient.invalidateQueries({ queryKey: ['expenses'] })

    if (errorCount === 0) {
      toast.success(`Rejected ${successCount} expense(s)`)
    } else {
      toast.warning(`Rejected ${successCount} expense(s), ${errorCount} failed`)
    }
  }

  const handleBulkDeleteClick = () => {
    if (selectedExpenses.size === 0) {
      toast.error('No expenses selected')
      return
    }

    // Filter to only draft expenses
    const allExpenses = Array.isArray(expenses) ? expenses : expenses?.items || expenses?.data || []
    const selectedExpenseIds = Array.from(selectedExpenses)
    const draftExpenses = allExpenses.filter((e: any) => 
      selectedExpenseIds.includes(e.id) && (e.status || 'draft').toLowerCase() === 'draft'
    )
    const nonDraftExpenses = allExpenses.filter((e: any) => 
      selectedExpenseIds.includes(e.id) && (e.status || 'draft').toLowerCase() !== 'draft'
    )

    if (draftExpenses.length === 0) {
      toast.error('No draft expenses selected. Only draft expenses can be deleted.')
      return
    }

    if (nonDraftExpenses.length > 0) {
      toast.warning(`${nonDraftExpenses.length} selected expense(s) are not in draft status and will be skipped.`)
    }

    setBulkDeleteConfirmOpen(true)
  }

  const handleBulkDeleteConfirm = async () => {
    if (selectedExpenses.size === 0) {
      return
    }

    setBulkDeleting(true)

    try {
      // Filter to only draft expenses
      const allExpenses = Array.isArray(expenses) ? expenses : expenses?.items || expenses?.data || []
      const selectedExpenseIds = Array.from(selectedExpenses)
      const draftExpenses = allExpenses.filter((e: any) => 
        selectedExpenseIds.includes(e.id) && (e.status || 'draft').toLowerCase() === 'draft'
      )

      // Delete all expenses in parallel (all at once, not one by one)
      // Promise.all executes all deletions simultaneously for maximum speed
      const deletePromises = draftExpenses.map((expense: any) => 
        expenseApi.deleteExpense(expense.id)
          .then(() => ({ success: true, id: expense.id }))
          .catch((error: any) => ({ success: false, id: expense.id, error }))
      )

      // Wait for all parallel deletions to complete
      const results = await Promise.all(deletePromises)
      
      let successCount = 0
      let errorCount = 0

      results.forEach((result) => {
        if (result.success) {
          successCount++
        } else {
          errorCount++
        }
      })

      setSelectedExpenses(new Set())
      setBulkDeleteConfirmOpen(false)
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['budget-analytics'] })

      // Show single summary toast after all deletions complete
      if (draftExpenses.length > 0) {
        if (errorCount === 0) {
          toast.success(`Successfully deleted ${successCount} expense(s)`)
        } else {
          toast.warning(`Deleted ${successCount} expense(s), ${errorCount} failed`)
        }
      }
    } catch (error) {
      toast.error('Failed to delete expenses')
    } finally {
      setBulkDeleting(false)
    }
  }

  const handleSingleDeleteConfirm = () => {
    if (expenseToDelete) {
      deleteExpense.mutate(expenseToDelete.id, {
        onSuccess: () => {
          setDeleteConfirmOpen(false)
          setExpenseToDelete(null)
        }
      })
    }
  }

  // Mutations
  const createCategory = useMutation({
    mutationFn: async (data: any) => expenseApi.createExpenseCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] })
      queryClient.invalidateQueries({ queryKey: ['budget-analytics'] }) // Invalidate analytics to update count
      setIsCreateCategoryOpen(false)
      toast.success('Expense category created successfully')
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Failed to create expense category'
      toast.error(errorMessage)
    }
  })

  const updateCategory = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => expenseApi.updateExpenseCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] })
      queryClient.invalidateQueries({ queryKey: ['budget-analytics'] }) // Invalidate analytics to update count
      setIsCreateCategoryOpen(false)
      setEditingCategory(null)
      toast.success('Expense category updated successfully')
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Failed to update expense category'
      toast.error(errorMessage)
    }
  })

  const createBudget = useMutation({
    mutationFn: async (data: any) => expenseApi.createBudget(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] })
      queryClient.invalidateQueries({ queryKey: ['budget-analytics'] })
      setIsCreateBudgetOpen(false)
      setEditingBudget(null)
      toast.success('Budget created successfully')
    },
    onError: (error) => {
      toast.error('Failed to create budget')
    }
  })

  const updateBudget = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => expenseApi.updateBudget(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] })
      queryClient.invalidateQueries({ queryKey: ['budget-analytics'] })
      setIsCreateBudgetOpen(false)
      setEditingBudget(null)
      toast.success('Budget updated successfully')
    },
    onError: (error) => {
      toast.error('Failed to update budget')
    }
  })

  const createRule = useMutation({
    mutationFn: async (data: any) => expenseApi.createExpenseRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-rules'] })
      setIsCreateRuleOpen(false)
      toast.success('Expense rule created successfully')
    },
    onError: (error) => {
      toast.error('Failed to create expense rule')
    }
  })

  const updateRule = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => expenseApi.updateExpenseRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-rules'] })
      setIsCreateRuleOpen(false)
      setEditingRule(null)
      toast.success('Expense rule updated successfully')
    },
    onError: (error) => {
      toast.error('Failed to update expense rule')
    }
  })
  
  const createExpense = useMutation({
    mutationFn: async (data: any) => {
      return await expenseApi.createExpense({
        ...data,
        companyId: data.companyId || getCompanyId()
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['budget-analytics'] })
      setIsCreateExpenseOpen(false)
      toast.success('Expense created successfully')
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Failed to create expense'
      toast.error(errorMessage)
    }
  })

  const deleteRule = useMutation({
    mutationFn: async (id: string) => expenseApi.deleteExpenseRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-rules'] })
      toast.success('Expense rule deleted')
    },
    onError: () => toast.error('Failed to delete expense rule')
  })

  const updateExpense = useMutation({
    mutationFn: async (data: any) => expenseApi.updateExpense(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      setEditOpen(false)
      setSelectedExpense(null)
      toast.success('Expense updated successfully')
    },
    onError: (error: any) => {
      // Handle budget exceeded error
      if (error.message?.startsWith('Budget exceeded:')) {
        toast.error(error.message, { duration: 10000 })
      } else if (error?.response?.data?.error?.startsWith('Budget exceeded:')) {
        toast.error(error.response.data.error, { duration: 10000 })
      } else {
        const errorMessage = error?.response?.data?.error || 'Failed to update expense'
        toast.error(errorMessage)
      }
    }
  })

  const deleteExpense = useMutation({
    mutationFn: async (id: string) => expenseApi.deleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['budget-analytics'] })
      toast.success('Expense deleted successfully')
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Failed to delete expense'
      toast.error(errorMessage)
    }
  })

  // Helper function to check if expense has journal entries
  const hasJournalEntries = async (expenseId: string): Promise<boolean> => {
    try {
      const entries = await expenseJournalApi.getJournalEntries(expenseId)
      return entries && entries.length > 0
    } catch (error) {
      return false
    }
  }

  const generateJournalEntry = useMutation({
    mutationFn: async ({ expenseId, silent = false }: { expenseId: string; silent?: boolean }) => {
      // First check if journal entries already exist
      const existingEntries = await expenseJournalApi.getJournalEntries(expenseId)
      if (existingEntries && existingEntries.length > 0) {
        // For silent mode during bulk operations, don't throw error, just return silently
        if (silent) {
          return { expenseId, skipped: true }
        }
        throw new Error('Journal entries already exist for this expense')
      }
      
      // Get the expense details
      const expense = await expenseApi.getExpenseById(expenseId)
      if (!expense) {
        throw new Error('Expense not found')
      }
      
      // Create journal entry by updating the expense (this will trigger journal creation)
      return await expenseApi.updateExpense(expenseId, { 
        ...expense, 
        status: expense.status === 'draft' ? 'submitted' : expense.status 
      })
    },
    onSuccess: (data, variables) => {
      const { expenseId, silent = false } = variables;
      // Skip if this was silently skipped (journal entries already exist)
      if ((data as any)?.skipped) {
        return
      }
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['expense-journal-entries', expenseId] })
      if (!silent) {
      toast.success('Journal entry generated successfully')
      }
    },
    onError: (error: any, variables) => {
      const { silent = false } = variables;
      // Suppress "journal entries already exist" error during bulk operations
      if (silent && error?.message?.includes('Journal entries already exist')) {
        return
      }
      const errorMessage = error?.message || 'Failed to generate journal entry'
      if (!silent) {
      toast.error(errorMessage)
      }
    }
  })

  // Form setup
  const categoryForm = useForm({
    resolver: zodResolver(categorySchema),
    defaultValues: {}
  })

  const expenseForm = useForm({
    resolver: zodResolver(z.object({
      companyId: z.string().min(1, 'Company is required'),
      expenseDate: z.string().min(1, 'Expense date is required'),
      referenceNumber: z.string().optional(), // Invoice/Receipt #
      currency: z.string().optional(),
      totalAmount: z.coerce.number().min(0).optional(), // Auto-calculated from line items
      taxAmount: z.coerce.number().min(0).optional(), // Auto-calculated from line items
      // Line items - now required and contains all details
      lineItems: z.array(z.object({
        itemCode: z.string().optional(),
        categoryId: z.string().optional(), // Category at line item level
        budgetId: z.string().optional(), // Budget at line item level
        description: z.string().min(1, 'Item description is required'),
        vendorId: z.string().optional(), // Vendor at line item level
        vendorName: z.string().optional(),
        quantity: z.coerce.number().positive('Quantity must be positive'),
        taxRate: z.coerce.number().min(0).max(100).optional(),
        taxType: z.string().optional(),
        unitPrice: z.coerce.number().positive('Unit price must be greater than 0')
      })).min(1, 'At least one line item is required')
    })),
    defaultValues: {
      companyId: '',
      expenseDate: new Date().toISOString().split('T')[0],
      referenceNumber: '',
      currency: 'Frw',
      totalAmount: 0,
      taxAmount: 0,
      lineItems: []
    }
  })

  const budgetForm = useForm({
    resolver: zodResolver(budgetSchema),
    defaultValues: {
      period: 'monthly' as const
    }
  })

  // Initialize form when editing budget changes
  useEffect(() => {
    if (editingBudget) {
      budgetForm.reset({
        companyId: (editingBudget as any).companyId || getCompanyId(), // ✅ Use getCompanyId() as fallback
        categoryId: editingBudget.category?.id || '',
        name: editingBudget.name || '',
        description: editingBudget.description || '',
        period: editingBudget.period || 'monthly',
        startDate: editingBudget.startDate || '',
        endDate: editingBudget.endDate || '',
        amount: editingBudget.amount || 0,
        alertThreshold: editingBudget.alertThreshold || 80
      })
    } else {
      budgetForm.reset({
        companyId: getCompanyId(), // ✅ Set companyId for new budgets
        period: 'monthly' as const
      })
    }
  }, [editingBudget, budgetForm])

  // Cleanup OCR preview URL on unmount
  useEffect(() => {
    return () => {
      if (ocrPreviewUrl) {
        URL.revokeObjectURL(ocrPreviewUrl)
      }
    }
  }, [ocrPreviewUrl])

  // Convert currency to Frw when analytics data is available
  // Note: Expenses are stored in USD, so we convert from USD to Frw (RWF)
  useEffect(() => {
    const convertToFrw = async () => {
      if (!analytics) return
      
      setConvertingCurrency(true)
      try {
        // Convert Total Budgeted amount from USD to Frw (RWF)
        // Expenses are stored in USD, so we always convert from USD to RWF
        if (analytics.totalBudgetedAmount && analytics.totalBudgetedAmount > 0) {
          try {
            const budgetedConversion = await bankingApi.convertCurrency(
              analytics.totalBudgetedAmount,
              'USD', // Source currency: USD (expenses are stored in USD)
              'FRW'  // Target currency: Rwandan Franc (FRW is the ISO code)
            )
            const convertedAmount = budgetedConversion?.conversion?.convertedAmount || budgetedConversion?.convertedAmount || analytics.totalBudgetedAmount
            setConvertedBudgeted(convertedAmount)
          } catch (error) {
            // Fallback to original amount if conversion fails
            setConvertedBudgeted(analytics.totalBudgetedAmount)
          }
        } else {
          setConvertedBudgeted(0)
        }

        // Convert Total Spent amount from USD to Frw (RWF)
        // Expenses are stored in USD, so we always convert from USD to RWF
        if (analytics.totalSpentAmount && analytics.totalSpentAmount > 0) {
          try {
            const spentConversion = await bankingApi.convertCurrency(
              analytics.totalSpentAmount,
              'USD', // Source currency: USD (expenses are stored in USD)
              'FRW'  // Target currency: Rwandan Franc (FRW is the ISO code)
            )
            const convertedAmount = spentConversion?.conversion?.convertedAmount || spentConversion?.convertedAmount || analytics.totalSpentAmount
            setConvertedSpent(convertedAmount)
          } catch (error) {
            // Fallback to original amount if conversion fails
            setConvertedSpent(analytics.totalSpentAmount)
          }
        } else {
          setConvertedSpent(0)
        }
      } catch (error) {
        // Fallback to original amounts (display as USD if conversion fails)
        setConvertedBudgeted(analytics?.totalBudgetedAmount || 0)
        setConvertedSpent(analytics?.totalSpentAmount || 0)
      } finally {
        setConvertingCurrency(false)
      }
    }

    convertToFrw()
  }, [analytics])

  // Convert selected expenses total to RWF
  useEffect(() => {
    const convertSelectedTotalToRWF = async () => {
      if (selectedExpenses.size === 0) {
        setSelectedExpensesTotalRWF(null)
        return
      }

      if (!expenses) return

      setConvertingSelectedTotal(true)
      try {
        const expensesList = (Array.isArray(expenses) ? expenses : expenses?.items || expenses?.data || []) as any[]
        const selectedExpensesList = expensesList.filter(e => selectedExpenses.has(e.id))
        let totalRWF = 0

        for (const expense of selectedExpensesList) {
          const amount = Number(expense.totalAmount || expense.amount || 0)
          if (amount > 0) {
            const expenseCurrency = expense.currency || 'RWF'
            
            if (expenseCurrency.toUpperCase() === 'RWF' || expenseCurrency.toUpperCase() === 'FRW') {
              // Already in RWF
              totalRWF += amount
            } else {
              // Convert to RWF
              try {
                const conversion = await bankingApi.convertCurrency(
                  amount,
                  expenseCurrency.toUpperCase() === 'USD' ? 'USD' : expenseCurrency,
                  'FRW'
                )
                const convertedAmount = conversion?.conversion?.convertedAmount || conversion?.convertedAmount || amount
                totalRWF += convertedAmount
              } catch (error) {
                // Fallback: assume 1:1 if conversion fails (or use a default rate)
                totalRWF += amount
              }
            }
          }
        }

        setSelectedExpensesTotalRWF(totalRWF)
      } catch (error) {
        // Calculate raw total as fallback
        const expensesList = (Array.isArray(expenses) ? expenses : expenses?.items || expenses?.data || []) as any[]
        const rawTotal = expensesList
          .filter(e => selectedExpenses.has(e.id))
          .reduce((sum, e) => sum + Number(e.totalAmount || e.amount || 0), 0)
        setSelectedExpensesTotalRWF(rawTotal)
      } finally {
        setConvertingSelectedTotal(false)
      }
    }

    convertSelectedTotalToRWF()
  }, [selectedExpenses, expenses])

  const ruleForm = useForm({
    resolver: zodResolver(ruleSchema),
    defaultValues: {
      companyId: '',
      categoryId: '',
      name: '',
      description: '',
      ruleType: 'approval_required' as const,
      amountLimit: undefined,
      blockedVendors: '',
      requireApproval: true,
      notifyManager: false,
      autoReject: false,
      conditions: '',
      actions: '',
      priority: 1
    }
  })

  // Computed values
  const filteredCategories = React.useMemo(() => {
    try {
  if (!categories) return []
  // API may return an array or a paginated object { items: [] } or { data: [] }
  if (Array.isArray(categories)) return categories
  if (categories.items) return categories.items
  if ((categories as any).data) return (categories as any).data
  return []
    } catch (error) {
      return []
    }
  }, [categories])

  const filteredBudgets = React.useMemo(() => {
    try {
  if (!budgets) return []
  if (Array.isArray(budgets)) return budgets
  if (budgets.items) return budgets.items
  if ((budgets as any).data) return (budgets as any).data
  return []
    } catch (error) {
      return []
    }
  }, [budgets])

  const filteredRules = React.useMemo(() => {
    try {
  if (!rules) return []
  if (Array.isArray(rules)) return rules
  if (rules.items) return rules.items
  if ((rules as any).data) return (rules as any).data
  return []
    } catch (error) {
      return []
    }
  }, [rules])

  const glAccounts = React.useMemo(() => {
    try {
      if (!accountsData) return []
      if (Array.isArray(accountsData)) return accountsData
      if (accountsData.accounts) return accountsData.accounts
      if (accountsData.items) return accountsData.items
      if ((accountsData as any).data) return (accountsData as any).data
      return []
    } catch (error) {
      return []
    }
  }, [accountsData])

  // Filter expense accounts (typically type EXPENSE) - show only expense accounts
  const expenseAccounts = React.useMemo(() => {
    return glAccounts.filter((acc: any) => {
      // Check if account type is EXPENSE
      const accountType = acc.type?.code || acc.accountType?.code;
      return accountType === 'EXPENSE' && acc.isActive !== false;
    });
  }, [glAccounts])

  const getTaxTreatmentColor = (treatment?: string) => {
    switch (treatment) {
      case 'deductible': return 'bg-green-100 text-green-800'
      case 'non-deductible': return 'bg-red-100 text-red-800'
      case 'partially_deductible': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getBudgetUtilization = (budget: Budget) => {
    return (budget.spentAmount / budget.amount) * 100
  }

  const getBudgetStatus = (budget: Budget) => {
    const utilization = getBudgetUtilization(budget)
    if (utilization >= 100) return 'over-budget'
    if (utilization >= 80) return 'near-limit'
    return 'within-budget'
  }

  const getBudgetStatusColor = (status: string) => {
    switch (status) {
      case 'over-budget': return 'bg-red-100 text-red-800'
      case 'near-limit': return 'bg-yellow-100 text-yellow-800'
      case 'within-budget': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const toggleCategoryExpansion = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId)
    } else {
      newExpanded.add(categoryId)
    }
    setExpandedCategories(newExpanded)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setExpenseStatus('all')
    setExpenseCategoryId('')
    setExpenseStartDate('')
    setExpenseEndDate('')
    setCurrentPage(1)
  }

  const onSubmitCategory = (data: any) => {
    // Validate parent category selection
    if (editingCategory && data.parentId && data.parentId !== 'none') {
      // Prevent selecting self as parent
      if (data.parentId === editingCategory.id) {
        toast.error('A category cannot be its own parent')
        return
      }
      
      // Prevent circular references - check if selected parent is a child of this category
      const currentCategory = filteredCategories.find(c => c.id === editingCategory.id)
      if (currentCategory) {
        const childIds = getAllChildCategoryIds(currentCategory, filteredCategories)
        if (childIds.includes(data.parentId)) {
          toast.error('Cannot select a child category as parent (would create circular reference)')
          return
        }
      }
    }
    
    // Convert "none" to undefined for parentId
    if (data.parentId === 'none' || data.parentId === '') {
      data.parentId = undefined
    }
    
    if (editingCategory) {
      // Update existing category
      updateCategory.mutate({ id: editingCategory.id, data })
    } else {
      // Create new category
      createCategory.mutate(data)
    }
  }

  const onSubmitBudget = (data: any) => {
    if (editingBudget) {
      // Update existing budget
      updateBudget.mutate({ id: editingBudget.id, data })
    } else {
      // Create new budget
      createBudget.mutate(data)
    }
  }

  const onSubmitRule = (data: any) => {
    // Convert user-friendly fields to JSON
    const processedData = { ...data }
    
    // Generate conditions JSON based on rule type
    switch (data.ruleType) {
      case 'amount_limit':
        processedData.conditions = JSON.stringify({
          amount: data.amountLimit || 0
        })
        break
      case 'vendor_restriction':
        processedData.conditions = JSON.stringify({
          vendors: data.blockedVendors ? data.blockedVendors.split(',').map((v: string) => v.trim()) : []
        })
        break
      case 'approval_required':
        processedData.conditions = JSON.stringify({})
        break
    }
    
    // Generate actions JSON based on user selections
    const actions: any = {}
    if (data.requireApproval) actions.require_approval = true
    if (data.notifyManager) actions.notify_manager = true
    if (data.autoReject) actions.auto_reject = true
    
    processedData.actions = JSON.stringify(actions)
    
    // Remove user-friendly fields before sending to API
    delete processedData.amountLimit
    delete processedData.blockedVendors
    delete processedData.requireApproval
    delete processedData.notifyManager
    delete processedData.autoReject
    
    if (editingRule) {
      updateRule.mutate({ id: (editingRule as any).id, data: processedData })
    } else {
      createRule.mutate(processedData)
    }
  }

  const onSubmitExpense = (data: any) => {
    // Line items are now required - calculate totals from them
    if (!data.lineItems || !Array.isArray(data.lineItems) || data.lineItems.length === 0) {
      toast.error('Please add at least one line item')
      return
    }
    
    // Calculate totals from line items
    const baseAmount = data.lineItems.reduce((sum: number, item: any) => {
      const qty = Number(item.quantity || 1);
      const price = Number(item.unitPrice || 0);
      return sum + (qty * price);
    }, 0);
    
    const taxAmount = data.lineItems.reduce((sum: number, item: any) => {
      const qty = Number(item.quantity || 1);
      const price = Number(item.unitPrice || 0);
      const taxRate = Number(item.taxRate || 0);
      return sum + (qty * price * (taxRate / 100));
    }, 0);
    
    const totalAmount = baseAmount + taxAmount;
    
    // Generate description from line items if not provided
    const description = data.lineItems.map((item: any) => item.description).filter(Boolean).join(', ') || 'Expense with line items';
    
    // Clean up data - expense header only has: date, reference, totals, company
    // Category, description, vendor are at line item level
    const cleanedData = {
      companyId: data.companyId,
      expenseDate: data.expenseDate,
      referenceNumber: data.referenceNumber || undefined,
      currency: data.currency || 'Frw',
      amount: baseAmount,
      taxAmount,
      totalAmount,
      description, // Generated from line items
      // Line items with all details (category, description, vendor, budget, etc.)
      lineItems: data.lineItems.map((item: any) => ({
        itemCode: item.itemCode || undefined,
        categoryId: item.categoryId || undefined,
        budgetId: item.budgetId || undefined, // Budget at line item level
        description: item.description,
        vendorId: item.vendorId || undefined,
        vendorName: item.vendorName || undefined,
        quantity: Number(item.quantity || 1),
        taxRate: Number(item.taxRate || 0),
        taxType: item.taxType || (item.taxRate > 0 ? 'B' : 'A'),
        unitPrice: Number(item.unitPrice || 0)
      }))
    };
    
    // Create expense as draft - user can approve it later
    createExpense.mutate(cleanedData)
  }

  // OCR Functions
  const handleOcrFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (!file) return
    
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }
    
    setOcrFile(file)
    if (ocrPreviewUrl) URL.revokeObjectURL(ocrPreviewUrl)
    const url = URL.createObjectURL(file)
    setOcrPreviewUrl(url)
    
    // If triggered from main page (not from expense creation dialog), auto-process and open dialog
    const inputId = e.target.id
    if (inputId === 'ocr-receipt-upload-main' || inputId === 'ocr-receipt-upload-empty') {
      // Auto-process OCR and open expense creation dialog
      await processReceiptOCRAndOpenDialog(file)
    }
  }

  const processReceiptOCR = async (file?: File) => {
    const fileToProcess = file || ocrFile
    if (!fileToProcess) return
    
    setOcrExtracting(true)
    try {
      // Lazy load Tesseract.js from CDN
      const loadTesseract = (): Promise<any> => new Promise((resolve, reject) => {
        if (typeof window !== 'undefined' && (window as any).Tesseract) {
          return resolve((window as any).Tesseract)
        }
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@4.0.2/dist/tesseract.min.js'
        script.async = true
        script.onload = () => resolve((window as any).Tesseract)
        script.onerror = reject
        document.head.appendChild(script)
      })
      
      const Tesseract = await loadTesseract()
      const result = await Tesseract.recognize(fileToProcess, 'eng', { logger: () => {} })
      const text = result?.data?.text || ''
      
      if (text) {
        // Extract data from OCR text
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
        
        // Extract vendor name (usually first line)
        const maybeVendor = lines[0] || ''
        
        // Extract amount (look for currency patterns)
        const amountMatch = text.match(/\$?\s*([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})|[0-9]+\.[0-9]{2})\b/g)
        let extractedAmount = ''
        if (amountMatch) {
          const last = amountMatch[amountMatch.length - 1]
            .replace(/\$/g, '')
            .replace(/,/g, '')
            .trim()
          extractedAmount = last
        }
        
        // Extract date
        const dateMatch = text.match(/\b(20[0-9]{2}[-\/.][0-1]?[0-9][-\/.][0-3]?[0-9]|[0-3]?[0-9][-\/.][0-1]?[0-9][-\/.](20[0-9]{2}))\b/)
        let extractedDate = new Date().toISOString().split('T')[0]
        if (dateMatch) {
          const raw = dateMatch[0].replace(/[.]/g, '-').replace(/[\/]/g, '-')
          const parts = raw.split('-')
          if (parts[0].length === 4) {
            extractedDate = raw
          } else {
            extractedDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`
          }
        }
        
        // Extract description (first few lines)
        const description = lines.slice(0, 3).join(' ') || maybeVendor
        
        // Note: This function is deprecated - OCR now uses extractReceiptData which opens the expense form with line items
        // The form fields are populated in proceedWithExtractedData instead
        
        toast.success('Receipt data extracted successfully!')
        return true
      } else {
        toast.warning('No text could be extracted from the receipt')
        return false
      }
    } catch (error) {
      toast.error('Failed to process receipt. Please try again or enter manually.')
      return false
    } finally {
      setOcrExtracting(false)
    }
  }

  const processReceiptOCRAndOpenDialog = async (file: File) => {
    // Extract data first - this will show the results modal
    await extractReceiptData(file)
  }

  const proceedWithExtractedData = () => {
    if (!ocrExtractedData) return
    
      // Extract tax from total amount if available
      const totalAmount = ocrExtractedData.amount || 0
    const itemsTotal = ocrExtractedData.items?.reduce((sum, item) => sum + item.amount, 0) || 0
      const extractedTax = totalAmount > itemsTotal ? totalAmount - itemsTotal : 0
      
    // Build line items from OCR extracted items
    let lineItems: any[] = []
    
    if (ocrExtractedData.items && ocrExtractedData.items.length > 0) {
      
      // Check if items have structured data (quantity, unitPrice, itemCode)
      const hasStructuredData = ocrExtractedData.items.some(item => 
        (item as any).quantity !== undefined || 
        (item as any).unitPrice !== undefined || 
        (item as any).itemCode !== undefined
      )
      
      if (hasStructuredData) {
        // Use structured data from invoice (quantity, unitPrice, itemCode already extracted)
        lineItems = ocrExtractedData.items.map((item: any) => {
          const quantity = item.quantity || 1
          const unitPrice = item.unitPrice || item.amount || 0
          const totalPrice = quantity * unitPrice
          
          // Calculate tax rate if tax amount is available
          let itemTaxRate = 0
          if (extractedTax > 0 && itemsTotal > 0) {
            // Distribute tax proportionally based on item total
            const itemTaxAmount = (totalPrice / itemsTotal) * extractedTax
            itemTaxRate = totalPrice > 0 ? (itemTaxAmount / totalPrice) * 100 : 0
          }
          
          return {
            itemCode: item.itemCode || undefined,
            categoryId: undefined,
            budgetId: undefined,
            description: item.name || '',
            vendorId: undefined,
            vendorName: ocrExtractedData.vendor || undefined,
            quantity: quantity,
            taxRate: parseFloat(itemTaxRate.toFixed(2)),
            taxType: itemTaxRate > 0 ? 'B' : 'A',
            unitPrice: unitPrice
          }
        })
      } else {
        // Simple receipt format - distribute tax proportionally
        lineItems = ocrExtractedData.items.map((item) => {
        // Calculate proportional tax for this item
        let itemTaxAmount = 0
        let itemTaxRate = 0
        
        if (extractedTax > 0 && itemsTotal > 0) {
          // Distribute tax proportionally based on item amount
          itemTaxAmount = (item.amount / itemsTotal) * extractedTax
          // Calculate tax rate percentage
          itemTaxRate = itemsTotal > 0 ? (itemTaxAmount / item.amount) * 100 : 0
        }
        
        return {
            itemCode: undefined,
            categoryId: undefined,
            budgetId: undefined,
            description: item.name || '',
            vendorId: undefined,
            vendorName: ocrExtractedData.vendor || undefined,
            quantity: 1,
          taxRate: parseFloat(itemTaxRate.toFixed(2)),
            taxType: itemTaxRate > 0 ? 'B' : 'A',
            unitPrice: item.amount || 0
          }
        })
      }
    } else {
      // No items detected - create a single line item from the total amount
      const taxRate = extractedTax > 0 && totalAmount > 0 ? (extractedTax / (totalAmount - extractedTax)) * 100 : 0
      
      lineItems = [{
        itemCode: undefined,
        categoryId: undefined,
        budgetId: undefined,
        description: ocrExtractedData.description || '',
        vendorId: undefined,
        vendorName: ocrExtractedData.vendor || undefined,
        quantity: 1,
        taxRate: parseFloat(taxRate.toFixed(2)),
        taxType: taxRate > 0 ? 'B' : 'A',
        unitPrice: totalAmount - extractedTax || totalAmount || 0
      }]
    }
    
    // Calculate totals from line items
    const baseAmount = lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0)
    const taxAmount = lineItems.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unitPrice
      return sum + (itemTotal * ((item.taxRate || 0) / 100))
    }, 0)
    const totalAmountCalculated = baseAmount + taxAmount
    
    // Generate description from line items
    const description = lineItems.map(item => item.description).filter(Boolean).join(', ') || 'Receipt expense'
    
    // Open expense creation dialog with extracted data and line items
    expenseForm.reset({
      companyId: getCompanyId(),
      expenseDate: ocrExtractedData.date || new Date().toISOString().split('T')[0],
        referenceNumber: '',
      currency: ocrExtractedData.currency || 'Frw',
            taxAmount: taxAmount,
      totalAmount: totalAmountCalculated,
      lineItems: lineItems
    })
    
    setOcrResultsOpen(false)
    setIsCreateExpenseOpen(true)
  }


  const loadSampleReceiptImage = async (imageName: string) => {
    try {
      // Fetch sample image from public folder
      const response = await fetch(`/${imageName}`)
      if (!response.ok) {
        toast.error(`Sample image ${imageName} not found`)
        return
      }
      
      const blob = await response.blob()
      
      // Check if blob is valid (has content)
      if (blob.size === 0) {
        toast.error(`Image file ${imageName} is empty or corrupted. Please use a valid image file.`)
        return
      }
      
      // Validate that it's actually an image file (if type is available)
      if (blob.type && !blob.type.startsWith('image/')) {
        toast.error(`Invalid image file: ${imageName}. Please use a valid image file.`)
        return
      }
      
      // Validate image by trying to load it
      const imageUrl = URL.createObjectURL(blob)
      const img = new Image()
      
      await new Promise((resolve, reject) => {
        img.onload = () => {
          URL.revokeObjectURL(imageUrl)
          resolve(true)
        }
        img.onerror = () => {
          URL.revokeObjectURL(imageUrl)
          reject(new Error('Invalid image format'))
        }
        img.src = imageUrl
      })
      
      const file = new File([blob], imageName, { type: blob.type || 'image/png' })
      
      // Set file and preview
      setOcrFile(file)
      if (ocrPreviewUrl) URL.revokeObjectURL(ocrPreviewUrl)
      const url = URL.createObjectURL(file)
      setOcrPreviewUrl(url)
      
      // Auto-process OCR and open dialog
      await processReceiptOCRAndOpenDialog(file)
    } catch (error) {
      toast.error(`Failed to load sample image: ${imageName}. The image may be corrupted or invalid. Please use a valid image file.`)
    }
  }

  // FREE Image Preprocessing for better OCR (using canvas)
  const preprocessImageForOCR = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const imgUrl = URL.createObjectURL(file)
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            URL.revokeObjectURL(imgUrl)
            resolve(file) // Return original if canvas not supported
            return
          }
          
          // Scale up for better OCR (2x resolution)
          canvas.width = img.width * 2
          canvas.height = img.height * 2
          
          // Draw image
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          
          // Get image data
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const data = imageData.data
          
          // Apply gentle enhancements (preserve all details - no threshold)
          for (let i = 0; i < data.length; i += 4) {
            // Convert to grayscale
            const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114
            
            // Gentle contrast enhancement (1.2x - very mild)
            const contrast = (gray - 128) * 1.2 + 128
            
            // No threshold - keep all grayscale values for better OCR
            const final = Math.max(0, Math.min(255, contrast))
            data[i] = data[i + 1] = data[i + 2] = final
            data[i + 3] = 255 // Keep alpha
          }
          
          // Put enhanced image data back
          ctx.putImageData(imageData, 0, 0)
          
          // Convert to blob
          canvas.toBlob((blob) => {
            URL.revokeObjectURL(imgUrl)
            if (blob) {
              const enhancedFile = new File([blob], file.name, { type: file.type || 'image/png' })
              resolve(enhancedFile)
            } else {
              resolve(file)
            }
          }, 'image/png', 1.0) // High quality PNG
        } catch (error) {
          URL.revokeObjectURL(imgUrl)
          resolve(file) // Return original on error
        }
      }
      
      img.onerror = () => {
        URL.revokeObjectURL(imgUrl)
        resolve(file) // Return original on error
      }
      
      img.src = imgUrl
    })
  }

  const extractReceiptData = async (file: File): Promise<{ vendor?: string; amount?: number; date?: string; description?: string; rawText?: string; items?: Array<{ name: string; amount: number; quantity?: number; unitPrice?: number; itemCode?: string }>; currency?: string } | null> => {
    setOcrExtracting(true)
    try {
      // Lazy load Tesseract.js from CDN
      const loadTesseract = (): Promise<any> => new Promise((resolve, reject) => {
        if (typeof window !== 'undefined' && (window as any).Tesseract) {
          return resolve((window as any).Tesseract)
        }
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@4.0.2/dist/tesseract.min.js'
        script.async = true
        script.onload = () => resolve((window as any).Tesseract)
        script.onerror = reject
        document.head.appendChild(script)
      })
      
      const Tesseract = await loadTesseract()
      
      // Use better OCR settings for invoices/receipts
      // Try PSM 3 (auto) first - it usually works best for invoices
      let text = ''
      
      try {
        // Step 1: Preprocess image for better OCR (FREE - using canvas)
        const preprocessedFile = await preprocessImageForOCR(file)
        
        // Step 2: Try OCR with multiple strategies (all free)
        let bestText = ''
        let bestConfidence = 0
        
        // Strategy 1: Original image + PSM 3 (auto) - try original first
        try {
          const result1 = await Tesseract.recognize(file, 'eng', { 
            logger: () => {},
            tessedit_pageseg_mode: '3',
          })
          const text1 = result1?.data?.text || ''
          const conf1 = result1?.data?.confidence || 0
          if (text1.length > bestText.length || (text1.length === bestText.length && conf1 > bestConfidence)) {
            bestText = text1
            bestConfidence = conf1
          }
        } catch (e) {
          // Strategy 1 failed, continue to next
        }
        
        // Strategy 2: Preprocessed + PSM 3 (auto)
        try {
          const result2 = await Tesseract.recognize(preprocessedFile, 'eng', { 
            logger: () => {},
            tessedit_pageseg_mode: '3',
          })
          const text2 = result2?.data?.text || ''
          const conf2 = result2?.data?.confidence || 0
          if (text2.length > bestText.length || (text2.length === bestText.length && conf2 > bestConfidence)) {
            bestText = text2
            bestConfidence = conf2
          }
        } catch (e) {
          // Strategy 2 failed, continue to next
        }
        
        // Strategy 3: Original + PSM 6 (uniform block)
        try {
          const result3 = await Tesseract.recognize(file, 'eng', { 
            logger: () => {},
            tessedit_pageseg_mode: '6', // Uniform block of text
          })
          const text3 = result3?.data?.text || ''
          const conf3 = result3?.data?.confidence || 0
          if (text3.length > bestText.length || (text3.length === bestText.length && conf3 > bestConfidence)) {
            bestText = text3
            bestConfidence = conf3
          }
        } catch (e) {
          // Strategy 3 failed, continue
        }
        
        text = bestText
        
        // If still no good result, try default
        if (!text || text.length < 50) {
          const defaultResult = await Tesseract.recognize(file, 'eng', { logger: () => {} })
          const defaultText = defaultResult?.data?.text || ''
          if (defaultText.length > text.length) {
            text = defaultText
          }
        }
        } catch (error) {
          // Final fallback
          try {
            const defaultResult = await Tesseract.recognize(file, 'eng', { logger: () => {} })
            text = defaultResult?.data?.text || ''
          } catch (fallbackError) {
            text = ''
          }
        }
      
      if (text) {
        // Extract data from OCR text
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
        const upperText = text.toUpperCase()
        
        // Extract vendor name - look for company names with "LTD", "Ltd", "INC", etc.
        let maybeVendor = ''
        const vendorPatterns = [
          /([A-Z][A-Z\s&]+(?:LTD|LIMITED|INC|INCORPORATED|LLC|CORP|CORPORATION))/i,
          /(INVOICE\s+TO|BILL\s+TO|CUSTOMER)[:\s]+([A-Z][A-Z\s&]+(?:LTD|LIMITED|INC)?)/i,
          /(SELLER|VENDOR|SUPPLIER)[:\s]+([A-Z][A-Z\s&]+(?:LTD|LIMITED|INC)?)/i
        ]
        
        for (const pattern of vendorPatterns) {
          const match = text.match(pattern)
          if (match) {
            maybeVendor = (match[2] || match[1] || '').trim()
            if (maybeVendor && maybeVendor.length > 3) break
          }
        }
        
        // Fallback: look for company names in first few lines
        if (!maybeVendor || maybeVendor.length < 3) {
          for (let i = 0; i < Math.min(10, lines.length); i++) {
            const line = lines[i]
            if (line && (line.includes('LTD') || line.includes('Ltd') || line.includes('INC') || line.includes('Limited'))) {
              // Extract company name (usually before "LTD" or similar)
              const companyMatch = line.match(/([A-Z][A-Z\s&]+?)(?:\s+(?:LTD|LIMITED|INC|INCORPORATED|LLC|CORP))/i)
              if (companyMatch && companyMatch[1]) {
                maybeVendor = companyMatch[1].trim()
                if (maybeVendor.length > 3) break
              } else if (line.length > 5 && line.length < 50) {
                maybeVendor = line.trim()
                break
              }
            }
          }
        }
        
        // Final fallback: use first meaningful line
        if (!maybeVendor || maybeVendor.length < 3) {
          maybeVendor = lines.find(line => line.length > 5 && line.length < 100 && !line.match(/^[0-9\s\-()]+$/)) || lines[0] || ''
        }
        
        // Extract amount (look for currency patterns including Frw)
        // First, try to find TOTAL line which usually contains the final amount
        let extractedAmount = 0
        const totalPatterns = [
          /TOTAL\s+(?:RWF|FRW|RWF|USD)?\s*:?\s*([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)/i,
          /TOTAL\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)\s*(?:RWF|FRW|RWF|USD)/i,
          /(?:TOTAL|GRAND\s+TOTAL)[:\s]+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)/i
        ]
        
        for (const pattern of totalPatterns) {
          const match = text.match(pattern)
          if (match && match[1]) {
            const amountStr = match[1].replace(/,/g, '').trim()
            extractedAmount = parseFloat(amountStr) || 0
            if (extractedAmount > 0) break
          }
        }
        
        // Fallback: if no TOTAL line found, get the largest amount from all matches
        if (extractedAmount === 0) {
          // Match amounts with or without decimals, with or without commas
          // Pattern: numbers with optional commas (thousands), optional decimals
          const amountMatch = text.match(/\b([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?|[0-9]+\.[0-9]{2})\b/g)
          if (amountMatch && amountMatch.length > 0) {
            // Get all amounts, remove commas, and find the largest (usually the total)
            const amounts = amountMatch.map(match => {
              const cleaned = match.replace(/,/g, '').trim()
              return parseFloat(cleaned) || 0
            }).filter(amt => amt > 0)
            
            if (amounts.length > 0) {
              // Get the largest amount (usually the total)
              extractedAmount = Math.max(...amounts)
            }
          }
        }
        
        // Extract date - handle multiple formats including DD-MM-YYYY (Rwanda format)
        let extractedDate = new Date().toISOString().split('T')[0]
        const datePatterns = [
          // DD-MM-YYYY or DD/MM/YYYY (Rwanda format)
          /\b([0-3]?[0-9])[-\/]([0-1]?[0-9])[-\/](20[0-9]{2})\b/,
          // YYYY-MM-DD or YYYY/MM/DD
          /\b(20[0-9]{2})[-\/]([0-1]?[0-9])[-\/]([0-3]?[0-9])\b/,
          // Date: DD-MM-YYYY
          /(?:DATE|Date)[:\s]+([0-3]?[0-9])[-\/]([0-1]?[0-9])[-\/](20[0-9]{2})/i
        ]
        
        for (const pattern of datePatterns) {
          const match = text.match(pattern)
          if (match) {
            let year, month, day
            if (match[3] && match[3].length === 4) {
              // YYYY-MM-DD format
              year = match[1]
              month = match[2].padStart(2, '0')
              day = match[3].padStart(2, '0')
          } else {
              // DD-MM-YYYY format (Rwanda)
              year = match[3]
              month = match[2].padStart(2, '0')
              day = match[1].padStart(2, '0')
            }
            
            // Validate date before using it
            const testDate = new Date(`${year}-${month}-${day}`)
            if (!isNaN(testDate.getTime())) {
              // Check if the date components match (to catch invalid dates like Feb 30)
              const dateStr = testDate.toISOString().split('T')[0]
              if (dateStr === `${year}-${month}-${day}`) {
                extractedDate = `${year}-${month}-${day}`
                break
              }
            }
          }
        }
        
        // Extract currency from receipt and convert if needed
        let extractedCurrency = 'Frw' // Default to RWF
        let needsConversion = false
        let conversionRate = 1
        // Note: upperText is already defined above
        
        // Check for USDT first (more specific) - keep as USDT, don't convert
        if (upperText.includes('USDT')) {
          extractedCurrency = 'USDT'
          needsConversion = false
        } else if (upperText.includes('USD') || text.includes('$')) {
          // If USD is detected, convert to RWF (for now)
          // Exchange rate: 1 USD = 1200 RWF (approximate, can be updated)
          conversionRate = 1200
          extractedAmount = extractedAmount * conversionRate
          extractedCurrency = 'Frw'
          needsConversion = true
        } else if (upperText.includes('RWF') || upperText.includes('FRW') || upperText.includes('RWF')) {
          extractedCurrency = 'Frw'
          needsConversion = false
        } else if (upperText.includes('EUR')) {
          extractedCurrency = 'EUR'
          needsConversion = false
        } else if (upperText.includes('GBP')) {
          extractedCurrency = 'GBP'
          needsConversion = false
        }
        
        // Extract items from receipt - handle both simple receipts and structured invoices
        const items: Array<{ name: string; amount: number; quantity?: number; unitPrice?: number; itemCode?: string }> = []
        
        // Try to detect structured invoice format (with columns: Item Code, Description, Qty, Unit Price, Total Price)
        // Use more flexible patterns to handle OCR errors - be more lenient
        const hasTableStructure = 
          /ITEM\s*CODE/i.test(text) || 
          /ITEM\s*DESCRIPTION/i.test(text) || 
          /QTY|QUANTITY/i.test(text) ||
          /UNIT\s*PRICE/i.test(text) || 
          /TOTAL\s*PRICE/i.test(text) ||
          /INGOICE|INVOICE/i.test(text) || // Handle OCR error "INGOICE" instead of "INVOICE"
          // Also check for patterns that suggest table structure (multiple numbers in sequence)
          /[A-Z0-9#]{6,}\s+[A-Z\s]{3,}\s+\d+\s+[AB]?\s*[\d,]+/i.test(text) ||
          // Check for invoice number pattern
          /(?:INVOICE|INGOICE)\s*(?:NO|NUMBER|#)?\s*:?\s*\d+/i.test(text)
        
        if (hasTableStructure) {
          // Parse structured invoice table
          // Look for table rows - typically have: ItemCode Description Qty Tax UnitPrice TotalPrice
          // Try multiple patterns to find table start
          let tableStartIndex = -1
          const tableStartPatterns = [
            /ITEM\s*CODE/i,
            /ITEM\s*DESCRIPTION/i,
            /QTY|QUANTITY/i,
            /UNIT\s*PRICE/i
          ]
          
          for (const pattern of tableStartPatterns) {
            const match = text.match(pattern)
            if (match && match.index !== undefined) {
              tableStartIndex = match.index
              break
            }
          }
          
          if (tableStartIndex > -1) {
            const tableSection = text.substring(tableStartIndex)
            const tableLines = tableSection.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
            
            // Skip header row and parse data rows
            for (let i = 1; i < tableLines.length; i++) {
              const line = tableLines[i]
              const upperLine = line.toUpperCase()
              
              // Skip if it's a summary/total line
              if (upperLine.includes('TOTAL') || upperLine.includes('SUBTOTAL') || 
                  (upperLine.includes('TAX') && !upperLine.match(/[A-Z]{3,}/)) || // Tax line but not a product name
                  upperLine.match(/^TOTAL\s+(?:RWF|FRW|USD)?/i)) {
                break
              }
              
              // Try multiple patterns to parse structured row
              // Pattern 1: ItemCode Description Qty Tax UnitPrice TotalPrice (full format)
              // Be more flexible with OCR errors - allow special chars in item code
              let structuredMatch = line.match(/^([A-Z0-9#]{6,})\s+(.+?)\s+(\d+)\s+([AB])\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)/i)
              
              // Pattern 1b: More flexible - handle OCR errors in item code (may have |, \, etc)
              if (!structuredMatch) {
                structuredMatch = line.match(/^([A-Z0-9#|\\\/]{6,})\s+(.+?)\s+(\d+)\s+([AB])\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)/i)
              }
              
              if (!structuredMatch) {
                // Pattern 2: More flexible - allow OCR errors in item code
                structuredMatch = line.match(/^([A-Z0-9#\s]{6,}?)\s+([A-Z][A-Z\s]{2,}?)\s+(\d+)\s+([AB])\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)/i)
              }
              
              if (!structuredMatch) {
                // Pattern 3: Without tax column - ItemCode Description Qty UnitPrice TotalPrice
                structuredMatch = line.match(/^([A-Z0-9#]{6,})\s+(.+?)\s+(\d+)\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)/i)
              }
              
              if (!structuredMatch) {
                // Pattern 4: Description Qty UnitPrice TotalPrice (no item code)
                structuredMatch = line.match(/^([A-Z][A-Z\s]{3,}?)\s+(\d+)\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)\s+([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)/i)
                if (structuredMatch) {
                  // Insert empty item code at position 1
                  structuredMatch = [structuredMatch[0], '', structuredMatch[1], structuredMatch[2], '', structuredMatch[3], structuredMatch[4]]
                }
              }
              
              if (structuredMatch) {
                const itemCode = (structuredMatch[1] || '').trim().replace(/\s+/g, '')
                const description = (structuredMatch[2] || '').trim()
                const quantity = parseFloat((structuredMatch[3] || '1').replace(/,/g, '')) || 1
                const unitPrice = parseFloat((structuredMatch[5] || structuredMatch[4] || '0').replace(/,/g, '')) || 0
                const totalPrice = parseFloat((structuredMatch[6] || structuredMatch[5] || structuredMatch[4] || '0').replace(/,/g, '')) || 0
                
                
                if (description && description.length > 2 && (totalPrice > 0 || unitPrice > 0)) {
                  // Skip tax lines
                  const upperDesc = description.toUpperCase()
                  const isTaxLine = /^(TAX|VAT|GST|SERVICE\s+TAX|TAX\s+AMOUNT|TAX\s+TOTAL|GOVERNMENT\s+TAX|SUBTOTAL)$/i.test(description) ||
                                   (upperDesc.includes('TAX') && !upperDesc.match(/[A-Z]{4,}/)) ||
                                   upperDesc.includes('SUBTOTAL')
                  
                  if (!isTaxLine) {
                    // Use totalPrice if available, otherwise calculate from unitPrice * quantity
                    const finalPrice = totalPrice > 0 ? totalPrice : (unitPrice * quantity)
                    let finalTotalPrice = finalPrice
                    if (needsConversion && conversionRate > 1) {
                      finalTotalPrice = finalPrice * conversionRate
                    }
                    
                    items.push({
                      name: description,
                      amount: finalTotalPrice,
                      quantity: quantity,
                      unitPrice: unitPrice > 0 ? unitPrice : (totalPrice / quantity),
                      itemCode: itemCode || undefined
                    })
                  } else {
                  }
                } else {
                }
              } else {
                // Try to extract any line with numbers that might be an item
                // Look for patterns like: "Text 100000" or "Text 1 100000 100000"
                const numberMatch = line.match(/([A-Z][A-Z\s]{3,}?)\s+(\d+)\s*([0-9]{1,3}(?:[,][0-9]{3})*(?:\.[0-9]{2})?)?/i)
                if (numberMatch && numberMatch[2]) {
                  const potentialPrice = parseFloat(numberMatch[2].replace(/,/g, ''))
                  if (potentialPrice > 100) { // Reasonable price threshold
                  }
                }
              }
            }
          } else {
          }
        }
        
        // If no structured items found, try simple receipt format
        if (items.length === 0) {
          
          // First, try to extract items from garbled OCR text by looking for number patterns
          // Look for lines that have both text and numbers (likely items)
          const potentialItemLines = lines.filter(line => {
            const trimmed = line.trim()
            if (!trimmed || trimmed.length < 3) return false
            
            const upperLine = trimmed.toUpperCase()
          
          // Skip if it's clearly a header/total/address line
          if (upperLine === 'TOTAL' || 
              upperLine.startsWith('TOTAL ') ||
              upperLine === 'SUBTOTAL' ||
              upperLine.startsWith('SUBTOTAL ') ||
                (upperLine === 'TAX' || upperLine.startsWith('TAX ')) ||
              upperLine.includes('DATE') ||
              upperLine.includes('TIME') ||
              upperLine.includes('PAYMENT') ||
              upperLine.includes('THANK') ||
              upperLine.includes('TEL:') ||
              upperLine.includes('PHONE:') ||
              (upperLine.includes('STREET') && !upperLine.includes('$')) ||
              (upperLine.includes('ADDRESS') && !upperLine.includes('$')) ||
              (upperLine.includes('USA') && !upperLine.includes('$')) ||
              (upperLine.includes('RWANDA') && !upperLine.includes('$')) ||
                /^[0-9\s\-()]+$/.test(trimmed)) { // Phone numbers or just numbers
            return false
          }
          
            // Look for lines that contain numbers (prices) - be VERY lenient with OCR errors
            // Accept any line with a number that could be a price (> 10)
            const hasNumber = /\d+/.test(trimmed)
            
            // Be more lenient with "text" - accept lines with any non-numeric characters
            // (OCR errors like |, \, @, etc. count as "text" indicators)
            const hasNonNumeric = /[^0-9\s,\.]/.test(trimmed)
            const numbers = trimmed.match(/\d+/g) || []
            const hasReasonablePrice = numbers.some(num => {
              const numVal = parseInt(num)
              return numVal > 10 && numVal < 100000000 // Reasonable price range
            })
          
          // Make sure it's not just a number (like a total line)
            // But allow some special chars (OCR errors)
            const isJustNumber = /^[0-9,\s.$@%\-:]+$/.test(trimmed)
            
            // Also check: if line has multiple numbers, it might be an item (qty + price)
            const hasMultipleNumbers = numbers.length > 1
            
            return hasNumber && (hasNonNumeric || hasMultipleNumbers) && hasReasonablePrice && !isJustNumber
          })
          
          
          const itemLines = potentialItemLines
          
          // Parse items from lines - be very flexible with OCR errors
          itemLines.forEach((line, index) => {
            const trimmed = line.trim()
            
            // Extract all numbers from the line (including decimals and commas)
            const allNumbers = trimmed.match(/[\d,]+\.?\d*/g) || []
            const numbers = allNumbers.map(n => parseFloat(n.replace(/,/g, ''))).filter(n => n > 0 && !isNaN(n))
            
            if (numbers.length === 0) {
              return
            }
            
            // Find the largest reasonable number (likely the price)
            // Filter out very small numbers (< 1) and very large numbers (> 100 million)
            const reasonableNumbers = numbers.filter(n => n >= 1 && n < 100000000)
            if (reasonableNumbers.length === 0) {
              return
            }
            
            const price = Math.max(...reasonableNumbers)
            
            // Skip if price is too small (likely not a real item price)
            // But be lenient - accept prices from 0.01 and up (for very small items)
            if (price < 0.01) {
              return
            }
            
            // Extract text part (everything before the numbers)
            // Try multiple patterns to extract item name
          let itemName = ''
            let itemPrice = price
            let quantity = 1
            
            // Pattern 1: Text followed by numbers (most common)
            // Be very flexible - match anything before numbers, even if it has special chars
            const textBeforeNumbers = trimmed.match(/^([^0-9]+?)(?:\s*[\d,]+\.?\d*)+/i)
            if (textBeforeNumbers && textBeforeNumbers[1]) {
              itemName = textBeforeNumbers[1].trim()
              // Clean up common OCR errors but keep some structure
              itemName = itemName
                .replace(/[|\\\/@%:\[\]()]+/g, ' ') // Remove common OCR error characters
                .replace(/[\.]{2,}/g, ' ') // Remove multiple dots
                .replace(/[—\-]{2,}/g, ' ') // Remove multiple dashes
                .replace(/\s+/g, ' ') // Normalize spaces
                .trim()
            }
            
            // Pattern 2: If no text before numbers, try to extract from anywhere
            // Extract any text-like content (even if garbled)
            if (!itemName || itemName.length < 2) {
              // Remove numbers but keep everything else (including OCR errors that might be letters)
              itemName = trimmed
                .replace(/[\d,\.]+/g, ' ') // Remove numbers and decimals
                .replace(/[|\\\/@%:\[\]()]+/g, ' ') // Remove special chars
                .replace(/[—\-]{2,}/g, ' ') // Remove multiple dashes
                .replace(/\s+/g, ' ') // Normalize spaces
                .trim()
            }
            
            // Pattern 3: If still no name, try to extract meaningful parts
            if (!itemName || itemName.length < 2) {
              // Split by common separators and take first meaningful part
              const parts = trimmed.split(/[\s|\\\/@%:\[\]()—\-]+/).filter(p => p.length > 1 && !/^\d+$/.test(p))
              if (parts.length > 0) {
                // Take first part that has letters or looks like text
                itemName = parts.find(p => /[A-Za-z]/.test(p) || p.length > 2) || parts[0] || 'Item'
          } else {
                // Last resort: use first few characters before numbers
                const beforeNum = trimmed.match(/^([^0-9]{3,})/)?.[1]
                itemName = beforeNum ? beforeNum.trim() : 'Item'
              }
            }
            
            // Final cleanup: if name is still garbled, try to extract readable parts
            if (itemName && itemName.length > 2) {
              // Try to find actual words (sequences of letters)
              const words = itemName.match(/[A-Za-z]{2,}/g)
              if (words && words.length > 0) {
                itemName = words.slice(0, 3).join(' ') // Use first few words
              } else {
                // If no words, try to find any meaningful sequence (even single letters)
                const meaningful = itemName.match(/[A-Za-z]+/g)
                if (meaningful && meaningful.length > 0) {
                  itemName = meaningful.slice(0, 3).join(' ')
                }
                // If still nothing, use a generic name based on price
                if (!itemName || itemName.length < 2) {
                  itemName = `Item ${price}`
                }
              }
            }
            
            // Try to detect quantity (small number before price)
            if (numbers.length > 1) {
              const sortedNumbers = [...reasonableNumbers].sort((a, b) => a - b)
              // If there's a small number (< 1000) before a large number, it might be quantity
              for (let i = 0; i < sortedNumbers.length - 1; i++) {
                if (sortedNumbers[i] < 1000 && sortedNumbers[i + 1] > sortedNumbers[i] * 5) {
                  quantity = Math.round(sortedNumbers[i])
                  itemPrice = sortedNumbers[i + 1]
                  break
                }
              }
            }
            
            
            // Skip if item name is just special characters or too garbled
            const cleanName = itemName.replace(/[^A-Za-z0-9\s]/g, '').trim()
            if (cleanName.length < 1) {
              // If no readable text, use generic name
              itemName = `Item ${itemPrice}`
            }
            
            // Skip tax lines
            const upperItemName = itemName.toUpperCase()
            const isTaxLine = /^(TAX|VAT|GST|SERVICE\s+TAX|TAX\s+AMOUNT|TAX\s+TOTAL|GOVERNMENT\s+TAX|SUBTOTAL)$/i.test(itemName) ||
                             (upperItemName.includes('TAX') && !upperItemName.match(/[A-Z]{4,}/)) ||
                             upperItemName.includes('SUBTOTAL')
            
            if (isTaxLine) {
              return
            }
            
            // Only add if it's not a tax line, has a valid amount
            if (itemPrice > 0) {
              // Convert item amount if currency conversion is needed (USD -> RWF)
              let finalItemPrice = itemPrice
              if (needsConversion && conversionRate > 1) {
                finalItemPrice = itemPrice * conversionRate
              }
              
              items.push({ 
                name: itemName, 
                amount: finalItemPrice,
                quantity: quantity > 1 ? quantity : undefined,
                unitPrice: quantity > 1 ? (finalItemPrice / quantity) : undefined
              })
            }
          })
          
        }
        
        // Extract description
        let description = ''
        
        // If items are detected, use item names as description (for invoices with products)
        if (items.length > 0) {
          description = items.map(item => item.name).join(', ')
        } else {
          // For expense receipts without items, look for "Description:" field or "PAYMENT DETAILS" section
          // First, try to find "Description:" field in the text
          const descriptionMatch = text.match(/Description:\s*\n?\s*(.+?)(?:\n|$|Payment|TOTAL|AUTHORIZED|Method)/i)
          if (descriptionMatch && descriptionMatch[1]) {
            description = descriptionMatch[1].trim()
            description = description.replace(/^:\s*/, '').trim()
          } else {
            // Look for text after "PAYMENT DETAILS" section
            const paymentDetailsIndex = text.toUpperCase().indexOf('PAYMENT DETAILS')
            if (paymentDetailsIndex !== -1) {
              const afterPaymentDetails = text.substring(paymentDetailsIndex + 'PAYMENT DETAILS'.length)
              const afterLines = afterPaymentDetails.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
              for (const line of afterLines) {
                const upper = line.toUpperCase()
                if (upper.includes('DESCRIPTION:') || 
                    upper.includes('PAYMENT METHOD') || 
                    upper.includes('TOTAL') ||
                    upper.includes('AUTHORIZED') ||
                    upper.includes('SIGNATURES') ||
                    line.length <= 3) {
                  continue
                }
                if (!upper.endsWith(':') && !upper.match(/^[A-Z\s]+$/)) {
                  description = line
                  break
                }
              }
            }
          }
          
          // Final fallback: use first few meaningful lines excluding vendor/address
          if (!description) {
            const meaningfulLines = lines
              .filter(line => {
                const upper = line.toUpperCase()
                return !upper.includes('TOTAL') && 
                       !upper.includes('SUBTOTAL') &&
                       !upper.includes('DATE') &&
                       !upper.includes('TIME') &&
                       !upper.includes('RECEIPT') &&
                       !upper.includes('PAYMENT') &&
                       !upper.includes('THANK') &&
                       !upper.includes('TEL') &&
                       !upper.includes('PHONE') &&
                       !upper.includes('STREET') &&
                       !upper.includes('ADDRESS') &&
                       !upper.includes('SUPPLIER') &&
                       !upper.includes('INFORMATION') &&
                       !upper.includes('AUTHORIZED') &&
                       !upper.includes('SIGNATURES') &&
                       !upper.includes('USA') &&
                       !upper.includes('RWANDA') &&
                       line.length > 3 &&
                       // Exclude lines that are just addresses or phone numbers
                       !/^[0-9\s\-()]+$/.test(line.trim())
              })
              .slice(0, 3)
            description = meaningfulLines.join(', ') || maybeVendor
          }
        }
        
        const extracted = {
          vendor: maybeVendor,
          amount: extractedAmount,
          date: extractedDate,
          description: description,
          rawText: text,
          items: items.length > 0 ? items : undefined,
          currency: extractedCurrency
        }
        
        // Store extracted data and show results modal
        setOcrExtractedData(extracted)
        setOcrResultsOpen(true)
        
        toast.success('Receipt data extracted successfully!')
        return extracted
      } else {
        toast.warning('No text could be extracted from the receipt')
        return null
      }
    } catch (error) {
      toast.error('Failed to process receipt. Please try again or enter manually.')
      return null
    } finally {
      setOcrExtracting(false)
    }
  }

  const clearOcrData = () => {
    setOcrFile(null)
    if (ocrPreviewUrl) {
      URL.revokeObjectURL(ocrPreviewUrl)
      setOcrPreviewUrl(null)
    }
  }

  const renderCategoryTree = (categories: ExpenseCategory[] = [], level = 0) => {
    return (categories || []).map((category) => (
      <div key={category.id}>
        <div 
          className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 ${
            level > 0 ? 'ml-6' : ''
          }`}
        >
          <div className="flex items-center space-x-3">
            {(category.children && category.children.length > 0) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleCategoryExpansion(category.id)}
              >
                {expandedCategories.has(category.id) ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </Button>
            )}
            <div 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: (category as any).color || '#6b7280' }}
            />
            <div>
              <h4 className="font-medium">{category.name}</h4>
              {category.description && (
                <p className="text-sm text-gray-600">{category.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {(category as any).taxTreatment && (
              <Badge className={getTaxTreatmentColor((category as any).taxTreatment)}>
                {(category as any).taxTreatment}
              </Badge>
            )}
            <Badge variant={(category as any).isActive ? 'default' : 'secondary'}>
              {(category as any).isActive ? 'Active' : 'Inactive'}
            </Badge>
            <Button 
              variant="outline" 
              size="sm" 
              aria-label={`Edit category ${category.name}`}
              onClick={() => {
                setEditingCategory(category)
                categoryForm.reset({
                  companyId: (category as any).companyId || getCompanyId(),
                  name: category.name,
                  description: category.description || '',
                  parentId: category.parentId || '',
                  color: (category as any).color || '',
                  icon: (category as any).icon || '',
                  taxTreatment: (category as any).taxTreatment || 'deductible',
                  approvalThreshold: (category as any).approvalThreshold || undefined
                })
                setIsCreateCategoryOpen(true)
              }}
            >
              <Edit className="w-4 h-4" />
            </Button>
          </div>
        </div>
  {expandedCategories.has(category.id) && (category.children && category.children.length > 0) && (
          <div className="mt-2">
            {renderCategoryTree(category.children, level + 1)}
          </div>
        )}
      </div>
    ))
  }

  return (
    <PageLayout>
      <div className="space-y-6">
        {/* Enhanced Header Section */}
        {/* <div className="bg-gradient-to-r from-cyan-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-cyan-100 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                  Expense Management
                </h1>
                <p className="text-slate-600 mt-2 text-lg font-medium">
                  Track, categorize, and manage your business expenses efficiently
                </p>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
                    <CheckCircle className="w-4 h-4" />
                    <span>System Active</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    Last sync: {new Date().toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => setReceiptOpen(true)}
                className="h-12 px-6 bg-white hover:bg-cyan-50 border-cyan-200 hover:border-cyan-300 transition-all duration-200"
              >
                <FileText className="w-4 h-4 mr-2" />
                Scan Receipt
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setReportOpen(true)}
                className="h-12 px-6 bg-white hover:bg-blue-50 border-blue-200 hover:border-blue-300 transition-all duration-200"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Export Report
              </Button>
              <Button 
                onClick={() => setIsCreateExpenseOpen(true)}
                className="h-12 px-8 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Expense
              </Button>
            </div>
          </div>
        </div> */}

        {/* Enhanced Analytics Cards */}
        {(analyticsLoading || analytics) && (
          <div className="grid grid-cols-4 md:grid-cols-4 gap-10 sm:gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-md transition-all duration-200 w-[270px]">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] sm:text-[10px] font-medium text-blue-600 mb-0.5"><TranslatedText text="Total Categories" /></p>
                    {analyticsLoading ? (
                      <div className="flex items-center gap-2">
                        <MoonLoader size="sm" color="blue" />
                        <span className="text-xs text-blue-600"><TranslatedText text="Loading..." /></span>
                  </div>
                    ) : (
                      <p className="text-base sm:text-lg font-bold text-blue-900 leading-tight">{analytics?.totalCategories || 0}</p>
                    )}
                    <p className="text-[8px] sm:text-[9px] text-blue-600 mt-0.5"><TranslatedText text="Expense categories" /></p>
                  </div>
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <FolderTree className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-md transition-all duration-200 w-[270px]">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] sm:text-[10px] font-medium text-green-600 mb-0.5"><TranslatedText text="Active Budgets" /></p>
                    {analyticsLoading ? (
                      <div className="flex items-center gap-2">
                        <MoonLoader size="sm" color="green" />
                        <span className="text-xs text-green-600"><TranslatedText text="Loading..." /></span>
                  </div>
                    ) : (
                      <p className="text-base sm:text-lg font-bold text-green-900 leading-tight">{analytics?.activeBudgets || 0}</p>
                    )}
                    <p className="text-[8px] sm:text-[9px] text-green-600 mt-0.5"><TranslatedText text="Currently active" /></p>
                  </div>
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <Target className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200 hover:shadow-md transition-all duration-200 w-[270px]">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] sm:text-[10px] font-medium text-teal-600 mb-0.5"><TranslatedText text="Total Budgeted" /></p>
                    {analyticsLoading || convertingCurrency ? (
                      <div className="flex items-center gap-2">
                        <MoonLoader size="sm" color="teal" />
                        <span className="text-xs text-teal-600"><TranslatedText text="Loading..." /></span>
                      </div>
                    ) : (
                      <p className="text-xs sm:text-sm md:text-base font-bold text-teal-900 leading-tight truncate">
                        {`${((convertedBudgeted ?? analytics?.totalBudgetedAmount) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Frw`}
                      </p>
                    )}
                    <p className="text-[8px] sm:text-[9px] text-teal-600 mt-0.5"><TranslatedText text="Budget allocation" /></p>
                  </div>
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-teal-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-md transition-all duration-200 w-[270px]">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] sm:text-[10px] font-medium text-orange-600 mb-0.5"><TranslatedText text="Total Spent" /></p>
                    {analyticsLoading || convertingCurrency ? (
                      <div className="flex items-center gap-2">
                        <MoonLoader size="sm" color="teal" />
                        <span className="text-xs text-orange-600"><TranslatedText text="Loading..." /></span>
                      </div>
                    ) : (
                      <p className="text-xs sm:text-sm md:text-base font-bold text-orange-900 leading-tight truncate">
                        {`${((convertedSpent ?? analytics?.totalSpentAmount) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Frw`}
                      </p>
                    )}
                    <p className="text-[8px] sm:text-[9px] text-orange-600 mt-0.5"><TranslatedText text="Expenses incurred" /></p>
                  </div>
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Enhanced Main Content */}
        <Card className="border-slate-200 -mt-2">
          <CardHeader className="pb-3 pt-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 mt-1"><TranslatedText text="Organize and track your business expenses" /></p>
              </div>
              <div className="flex items-center space-x-3">
                {activeTab === 'categories' && (
                  <Dialog open={isCreateCategoryOpen} onOpenChange={setIsCreateCategoryOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        onClick={() => {
                          setEditingCategory(null)
                          categoryForm.reset({
                            companyId: getCompanyId(),
                            name: '',
                            description: '',
                            parentId: '',
                            color: '#3B82F6',
                            icon: '',
                            taxTreatment: 'deductible',
                            approvalThreshold: undefined
                          })
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        <TranslatedText text="New Category" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingCategory ? <TranslatedText text="Edit Expense Category" /> : <TranslatedText text="Create Expense Category" />}</DialogTitle>
                        <DialogDescription>
                          {editingCategory ? <TranslatedText text="Update category information and settings" /> : <TranslatedText text="Create a new category to organize your expenses" />}
                        </DialogDescription>
                      </DialogHeader>
                      <Form {...categoryForm}>
                        <form onSubmit={categoryForm.handleSubmit(onSubmitCategory)} className="space-y-4">
                          <FormField
                            control={categoryForm.control}
                            name="companyId"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Company</FormLabel>
                                <FormControl>
                                  <Input 
                                    value={companyName} 
                                    disabled 
                                    className="bg-slate-50"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={categoryForm.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel><TranslatedText text="Category Name" /></FormLabel>
                                <FormControl>
                                  <Input placeholder={tSync("Office Supplies")} {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={categoryForm.control}
                            name="description"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel><TranslatedText text="Description" /></FormLabel>
                                <FormControl>
                                  <Textarea placeholder={tSync("Category description...")} {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={categoryForm.control}
                            name="parentId"
                            render={({ field }) => {
                              // Filter out the current category being edited and its children from parent options
                              let availableParentCategories = filteredCategories
                              
                              if (editingCategory) {
                                const currentCategory = filteredCategories.find(c => c.id === editingCategory.id)
                                if (currentCategory) {
                                  const childIds = getAllChildCategoryIds(currentCategory, filteredCategories)
                                  // Filter out self and all children
                                  availableParentCategories = filteredCategories.filter(
                                    cat => cat.id !== editingCategory.id && !childIds.includes(cat.id)
                                  )
                                } else {
                                  // If category not found, just filter out self
                                  availableParentCategories = filteredCategories.filter(
                                    cat => cat.id !== editingCategory.id
                                  )
                                }
                              }
                              
                              return (
                                <FormItem>
                                  <FormLabel><TranslatedText text="Parent Category (Optional)" /></FormLabel>
                                  <Select 
                                    value={field.value || 'none'} 
                                    onValueChange={(value) => {
                                      field.onChange(value === 'none' ? '' : value)
                                    }}
                                  >
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder={tSync("Select parent category")} />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent className="max-h-60 overflow-y-auto">
                                      <SelectItem value="none"><TranslatedText text="None (Top Level)" /></SelectItem>
                                      {availableParentCategories.length > 0 ? (
                                        availableParentCategories.map((category: any) => (
                                          <SelectItem key={category.id} value={category.id}>
                                            {category.name}
                                          </SelectItem>
                                        ))
                                      ) : (
                                        <SelectItem value="none" disabled>
                                          <TranslatedText text="No available parent categories" />
                                        </SelectItem>
                                      )}
                                    </SelectContent>
                                  </Select>
                                  <FormDescription>
                                    {editingCategory 
                                      ? <TranslatedText text="Cannot select this category or any of its children as parent" />
                                      : <TranslatedText text="Select a parent category to create a hierarchical structure" />}
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )
                            }}
                          />
                          
                          <FormField
                            control={categoryForm.control}
                            name="color"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel><TranslatedText text="Color" /></FormLabel>
                                <FormControl>
                                  <Input type="color" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={categoryForm.control}
                            name="taxTreatment"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel><TranslatedText text="Tax Treatment" /></FormLabel>
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder={tSync("Select tax treatment")} />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="deductible"><TranslatedText text="Fully Deductible" /></SelectItem>
                                    <SelectItem value="partially_deductible"><TranslatedText text="Partially Deductible" /></SelectItem>
                                    <SelectItem value="non-deductible"><TranslatedText text="Non-Deductible" /></SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={categoryForm.control}
                            name="approvalThreshold"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel><TranslatedText text="Approval Threshold ($)" /></FormLabel>
                                <FormControl>
                                  <Input type="number" step="0.01" placeholder="0.00" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <div className="flex justify-end space-x-2">
                            <Button type="button" variant="outline" onClick={() => {
                              setIsCreateCategoryOpen(false)
                              setEditingCategory(null)
                              categoryForm.reset()
                            }}>
                              <TranslatedText text="Cancel" />
                            </Button>
                            <Button type="submit" disabled={createCategory.status === 'pending' || updateCategory.status === 'pending'}>
                              {createCategory.status === 'pending' || updateCategory.status === 'pending' 
                                ? (editingCategory ? <TranslatedText text="Updating..." /> : <TranslatedText text="Creating..." />) 
                                : (editingCategory ? <TranslatedText text="Update Category" /> : <TranslatedText text="Create Category" />)}
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </DialogContent>
                  </Dialog>
                )}

                {activeTab === 'expenses' && (
                  <Button onClick={() => {
                    expenseForm.reset({
                      companyId: getCompanyId(),
                      expenseDate: new Date().toISOString().split('T')[0],
                      referenceNumber: '',
                      currency: 'Frw',
                      totalAmount: 0,
                      taxAmount: 0,
                      lineItems: []
                    })
                    setIsCreateExpenseOpen(true)
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="New Expense" />
                  </Button>
                )}
                
                {activeTab === 'budgets' && (
                  <Dialog open={isCreateBudgetOpen} onOpenChange={(open) => {
                    setIsCreateBudgetOpen(open)
                    if (!open) {
                      budgetForm.reset()
                      setEditingBudget(null)
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="w-4 h-4 mr-2" />
                        <TranslatedText text="New Budget" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="!max-w-7xl max-h-[95vh] overflow-hidden w-[95vw] flex flex-col">
                      <DialogHeader className="pb-6 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center">
                                <Target className="w-5 h-5 text-white" />
                          </div>
                          <div>
                                <DialogTitle className="text-2xl font-bold text-slate-900">
                                  {editingBudget ? <TranslatedText text="Edit Budget" /> : <TranslatedText text="Create New Budget" />}
                                </DialogTitle>
                                <DialogDescription className="text-slate-600 mt-1">
                                  {editingBudget ? <TranslatedText text="Update your budget settings and allocations" /> : <TranslatedText text="Set up a new budget to track and control expenses" />}
                                </DialogDescription>
                          </div>
                        </div>
                      </DialogHeader>
                      
                      <Form {...budgetForm}>
                        <form onSubmit={budgetForm.handleSubmit(onSubmitBudget)} className="flex flex-col h-full">
                            <div className="flex-1 overflow-y-auto max-h-[calc(95vh-200px)] scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400 relative">
                              
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-6 pr-2 pb-8">
                                  {/* Left Column - Basic Information */}
                                  <div className="lg:col-span-1 space-y-6">
                                    {/* Company & Category Selection */}
                                    <div className="bg-slate-50 rounded-xl p-6">
                                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                        <Building className="w-5 h-5 text-teal-600" />
                                        <TranslatedText text="Company & Category" />
                                      </h3>
                                      <div className="space-y-4">
                          <FormField
                            control={budgetForm.control}
                            name="companyId"
                            render={({ field }) => (
                              <FormItem>
                                              <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Company" /> *</FormLabel>
                                <FormControl>
                                  <Input 
                                    value={companyName} 
                                    disabled 
                                    className="bg-slate-50 h-12"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={budgetForm.control}
                            name="categoryId"
                            render={({ field }) => (
                              <FormItem>
                                              <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Category" /> *</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl>
                                                  <SelectTrigger className="h-12 bg-white border-slate-300">
                                      <SelectValue placeholder={tSync("Select category")} />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-h-60 overflow-y-auto">
                                    {filteredCategories.map((category: any) => (
                                      <SelectItem key={category.id} value={category.id}>
                                        {category.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                                      </div>
                                    </div>

                                    {/* Budget Details */}
                                    <div className="bg-slate-50 rounded-xl p-6">
                                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                    <DollarSign className="w-5 h-5 text-teal-600" />
                                    <TranslatedText text="Budget Details" />
                                  </h3>
                                  <div className="space-y-4">
                          <FormField
                            control={budgetForm.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                          <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Budget Name" /> *</FormLabel>
                                <FormControl>
                                            <Input placeholder={tSync("Q1 Office Supplies Budget")} className="h-12 bg-white border-slate-300" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={budgetForm.control}
                                      name="amount"
                            render={({ field }) => (
                              <FormItem>
                                          <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Amount" /> *</FormLabel>
                                <FormControl>
                                            <Input type="number" step="0.01" placeholder="0.00" className="h-12 bg-white border-slate-300" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                            <FormField
                              control={budgetForm.control}
                              name="period"
                              render={({ field }) => (
                                <FormItem>
                                          <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Period" /> *</FormLabel>
                                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                              <SelectTrigger className="h-12 bg-white border-slate-300">
                                        <SelectValue />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="monthly"><TranslatedText text="Monthly" /></SelectItem>
                                      <SelectItem value="quarterly"><TranslatedText text="Quarterly" /></SelectItem>
                                      <SelectItem value="yearly"><TranslatedText text="Yearly" /></SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                                  </div>
                                </div>

                          </div>
                          
                          {/* Right Column - Description & Dates */}
                          <div className="lg:col-span-1 space-y-6">
                                <div className="bg-slate-50 rounded-xl p-6">
                                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-teal-600" />
                                    <TranslatedText text="Budget Description" />
                                  </h3>
                            <FormField
                              control={budgetForm.control}
                                    name="description"
                              render={({ field }) => (
                                <FormItem>
                                        <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Description" /></FormLabel>
                                  <FormControl>
                                          <Textarea 
                                            placeholder={tSync("Describe the purpose and scope of this budget...")} 
                                            className="min-h-[120px] bg-white border-slate-300 resize-none" 
                                            {...field} 
                                          />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                                {/* Dates & Alerts */}
                                <div className="bg-slate-50 rounded-xl p-6">
                                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-teal-600" />
                                    <TranslatedText text="Dates & Alerts" />
                                  </h3>
                                  <div className="space-y-4">
                            <FormField
                              control={budgetForm.control}
                              name="startDate"
                              render={({ field }) => (
                                <FormItem>
                                          <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Start Date" /> *</FormLabel>
                                  <FormControl>
                                            <Input type="date" className="h-12 bg-white border-slate-300" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={budgetForm.control}
                              name="endDate"
                              render={({ field }) => (
                                <FormItem>
                                          <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="End Date" /></FormLabel>
                                  <FormControl>
                                            <Input type="date" className="h-12 bg-white border-slate-300" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          
                          <FormField
                            control={budgetForm.control}
                            name="alertThreshold"
                            render={({ field }) => (
                              <FormItem>
                                          <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Alert Threshold (%)" /></FormLabel>
                                <FormControl>
                                            <Input type="number" step="1" placeholder="80" className="h-12 bg-white border-slate-300" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                                  </div>
                                </div>
                          </div>
                        </div>
                        
                            </div>
                            
                            <div className="sticky bottom-0 bg-white border-t border-slate-200 p-6">
                              <div className="flex items-center justify-between w-full">
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  onClick={() => {
                              setIsCreateBudgetOpen(false)
                              setEditingBudget(null)
                                  }}
                                  className="h-12 px-6"
                                >
                              Cancel
                            </Button>
                                <Button 
                                  type="submit" 
                                  disabled={createBudget.status === 'pending' || updateBudget.status === 'pending'}
                                  className="h-12 px-8 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 shadow-lg hover:shadow-xl transition-all duration-200"
                                >
                                  {createBudget.status === 'pending' || updateBudget.status === 'pending' ? (
                                    <>
                                      <MoonLoader size="sm" className="mr-2" color="teal" />
                                      {editingBudget ? 'Updating...' : 'Creating...'}
                                    </>
                                  ) : (
                                    <>
                                      <Target className="w-4 h-4 mr-2" />
                                      {editingBudget ? 'Update Budget' : 'Create Budget'}
                                    </>
                                  )}
                            </Button>
                              </div>
                          </div>
                        </form>
                      </Form>
                    </DialogContent>
                  </Dialog>
                )}

                {/* View Budget Dialog */}
                <Dialog open={isViewBudgetOpen} onOpenChange={setIsViewBudgetOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Budget Details</DialogTitle>
                      <DialogDescription>View complete information about this budget</DialogDescription>
                    </DialogHeader>
                    {selectedBudget && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-500">Budget Name</label>
                            <p className="text-lg font-semibold">{selectedBudget.name}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Category</label>
                            <p className="text-lg">{selectedBudget.category?.name || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Period</label>
                            <p className="text-lg capitalize">{selectedBudget.period}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Status</label>
                            <p className="text-lg">
                              <Badge variant={getBudgetStatus(selectedBudget) === 'within-budget' ? 'default' : getBudgetStatus(selectedBudget) === 'near-limit' ? 'secondary' : 'destructive'}>
                                {getBudgetStatus(selectedBudget)}
                              </Badge>
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Budget Amount</label>
                            <p className="text-lg font-semibold text-green-600">{formatCurrency(selectedBudget.amount)}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Amount Spent</label>
                            <p className="text-lg font-semibold text-red-600">{formatCurrency(selectedBudget.spentAmount)}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Start Date</label>
                            <p className="text-lg">{format(new Date(selectedBudget.startDate), 'MMM dd, yyyy')}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">End Date</label>
                            <p className="text-lg">{format(new Date(selectedBudget.endDate), 'MMM dd, yyyy')}</p>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Description</label>
                          <p className="text-lg">{selectedBudget.description || 'No description provided'}</p>
                        </div>
                        <div className="pt-4">
                          <label className="text-sm font-medium text-gray-500">Utilization</label>
                          <div className="mt-2">
                            <Progress value={getBudgetUtilization(selectedBudget)} className="w-full" />
                            <p className="text-sm text-gray-600 mt-1">
                              {getBudgetUtilization(selectedBudget).toFixed(1)}% utilized
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-end pt-4">
                          <Button variant="outline" onClick={() => setIsViewBudgetOpen(false)}>
                            Close
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                
                {activeTab === 'rules' && (
                  <Dialog open={isCreateRuleOpen} onOpenChange={(open) => {
                    setIsCreateRuleOpen(open)
                    if (!open) {
                      setEditingRule(null)
                    }
                  }}>
                    <DialogTrigger asChild>
                  <Button onClick={() => {
                    setEditingRule(null)
                    ruleForm.reset({
                      companyId: '',
                      categoryId: '',
                      name: '',
                      description: '',
                      ruleType: 'approval_required' as const,
                      amountLimit: undefined,
                      blockedVendors: '',
                      requireApproval: true,
                      notifyManager: false,
                      autoReject: false,
                      conditions: '',
                      actions: '',
                      priority: 1
                    })
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="New Rule" />
                  </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                      <DialogHeader className="flex-shrink-0">
                        <DialogTitle>{editingRule ? <TranslatedText text="Edit Expense Rule" /> : <TranslatedText text="Create Expense Rule" />}</DialogTitle>
                        <DialogDescription>
                          {editingRule ? <TranslatedText text="Modify expense rule settings and conditions" /> : <TranslatedText text="Create automated rules for expense approval and validation" />}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex-1 overflow-y-auto px-1">
                        <Form {...ruleForm}>
                          <form onSubmit={ruleForm.handleSubmit(onSubmitRule)} className="space-y-4">
                          <FormField
                            control={ruleForm.control}
                            name="companyId"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel><TranslatedText text="Company" /></FormLabel>
                                <FormControl>
                                  <Input 
                                    value={companyName} 
                                    disabled 
                                    className="bg-slate-50"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={ruleForm.control}
                            name="categoryId"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel><TranslatedText text="Category" /></FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder={tSync("Select category")} />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-h-60 overflow-y-auto">
                                    {filteredCategories.map((category: any) => (
                                      <SelectItem key={category.id} value={category.id}>
                                        {category.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={ruleForm.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel><TranslatedText text="Rule Name" /></FormLabel>
                                <FormControl>
                                  <Input placeholder={tSync("High Amount Approval Rule")} {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={ruleForm.control}
                            name="description"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                  <Textarea placeholder="Rule description..." {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={ruleForm.control}
                            name="ruleType"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel><TranslatedText text="Rule Type" /></FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder={tSync("Select rule type")} />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="amount_limit"><TranslatedText text="Amount Limit" /></SelectItem>
                                    <SelectItem value="vendor_restriction"><TranslatedText text="Vendor Restriction" /></SelectItem>
                                    <SelectItem value="approval_required"><TranslatedText text="Approval Required" /></SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          {/* Conditional fields based on rule type */}
                          {ruleForm.watch('ruleType') === 'amount_limit' && (
                            <FormField
                              control={ruleForm.control}
                              name="amountLimit"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel><TranslatedText text="Maximum Amount" /></FormLabel>
                                  <FormControl>
                                    <Input type="number" step="0.01" placeholder="1000.00" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                          
                          {ruleForm.watch('ruleType') === 'vendor_restriction' && (
                            <FormField
                              control={ruleForm.control}
                              name="blockedVendors"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel><TranslatedText text="Blocked Vendors" /></FormLabel>
                                  <FormControl>
                                    <Input placeholder={tSync("Vendor1, Vendor2, Vendor3")} {...field} />
                                  </FormControl>
                                  <p className="text-sm text-gray-500"><TranslatedText text="Separate multiple vendors with commas" /></p>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                          
                          {/* Action checkboxes */}
                          <div className="space-y-3">
                            <h4 className="text-sm font-medium"><TranslatedText text="Actions" /></h4>
                            <FormField
                              control={ruleForm.control}
                              name="requireApproval"
                              render={({ field }) => (
                                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                                  <FormControl>
                                    <input
                                      type="checkbox"
                                      checked={field.value}
                                      onChange={field.onChange}
                                      className="rounded border-gray-300"
                                    />
                                  </FormControl>
                                  <FormLabel className="text-sm font-normal">
                                    <TranslatedText text="Require approval before payment" />
                                  </FormLabel>
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={ruleForm.control}
                              name="notifyManager"
                              render={({ field }) => (
                                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                                  <FormControl>
                                    <input
                                      type="checkbox"
                                      checked={field.value}
                                      onChange={field.onChange}
                                      className="rounded border-gray-300"
                                    />
                                  </FormControl>
                                  <FormLabel className="text-sm font-normal">
                                    <TranslatedText text="Notify manager when rule is triggered" />
                                  </FormLabel>
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={ruleForm.control}
                              name="autoReject"
                              render={({ field }) => (
                                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                                  <FormControl>
                                    <input
                                      type="checkbox"
                                      checked={field.value}
                                      onChange={field.onChange}
                                      className="rounded border-gray-300"
                                    />
                                  </FormControl>
                                  <FormLabel className="text-sm font-normal">
                                    <TranslatedText text="Automatically reject expenses that violate this rule" />
                                  </FormLabel>
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          <FormField
                            control={ruleForm.control}
                            name="priority"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel><TranslatedText text="Priority" /></FormLabel>
                                <FormControl>
                                  <Input type="number" min="1" placeholder="1" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </form>
                      </Form>
                      </div>
                      <div className="flex-shrink-0 border-t border-slate-200 p-4 bg-slate-50">
                        <div className="flex justify-end space-x-2">
                          <Button type="button" variant="outline" onClick={() => setIsCreateRuleOpen(false)}>
                            <TranslatedText text="Cancel" />
                          </Button>
                          <Button type="button" disabled={createRule.status === 'pending' || updateRule.status === 'pending'} onClick={ruleForm.handleSubmit(onSubmitRule)}>
                            {createRule.status === 'pending' || updateRule.status === 'pending' 
                              ? (editingRule ? <TranslatedText text="Updating..." /> : <TranslatedText text="Creating..." />) 
                              : (editingRule ? <TranslatedText text="Update Rule" /> : <TranslatedText text="Create Rule" />)}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                {activeTab === 'rules' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCreateRuleOpen(true)
                      ruleForm.reset({
                        companyId: '',
                        categoryId: '',
                        name: 'Require Approval',
                        description: 'All expenses require approval before payment',
                        ruleType: 'approval_required',
                        amountLimit: undefined,
                        blockedVendors: '',
                        requireApproval: true,
                        notifyManager: false,
                        autoReject: false,
                        conditions: JSON.stringify({}, null, 2),
                        actions: JSON.stringify({ require_approval: true }, null, 2),
                        priority: 1,
                      })
                    }}
                  >
                    <TranslatedText text="Require Approval Policy" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Enhanced Search and Filter Section */}
            <div className="px-6 py-2 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex-1 min-w-[300px]">
                    <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <Input
                      placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-9 bg-white border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={expenseStatus} onValueChange={setExpenseStatus}>
                    <SelectTrigger className="w-40 h-9 bg-white border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100">
                      <SelectValue placeholder={tSync("Status")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all"><TranslatedText text="All Status" /></SelectItem>
                      <SelectItem value="draft"><TranslatedText text="Draft" /></SelectItem>
                      <SelectItem value="pending"><TranslatedText text="Pending" /></SelectItem>
                      <SelectItem value="approved"><TranslatedText text="Approved" /></SelectItem>
                      <SelectItem value="rejected"><TranslatedText text="Rejected" /></SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={expenseCategoryId || 'all-categories'} onValueChange={(v) => setExpenseCategoryId(v === 'all-categories' ? '' : v)}>
                    <SelectTrigger className="w-48 h-9 bg-white border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100">
                      <SelectValue placeholder={tSync("Category")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      <SelectItem value="all-categories"><TranslatedText text="All Categories" /></SelectItem>
                      {filteredCategories.map((category: any) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" className="h-9 px-3 bg-white border-slate-300">
                    <Filter className="w-3.5 h-3.5 mr-1.5" />
                    <TranslatedText text="Filters" />
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-9 px-3 bg-white border-slate-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
                    onClick={clearFilters}
                  >
                    <X className="w-3.5 h-3.5 mr-1.5" />
                    <TranslatedText text="Clear Filters" />
                  </Button>
                    </div>
                  </div>
                </div>

            {/* Enhanced Navigation Tabs */}
            <div className="px-6 py-2">
              <SegmentedTabs
                tabs={[
                  { id: 'categories', label: categoriesLabel, icon: FolderTree },
                  { id: 'budgets', label: budgetsLabel, icon: Target },
                  { id: 'rules', label: rulesLabel, icon: Settings },
                  { id: 'expenses', label: expensesLabel, icon: DollarSign },
                ]}
                value={activeTab}
                onChange={(id) => setActiveTab(id)}
              />
            </div>

            {activeTab === 'categories' && (
              <div className="px-6 py-6">
                <div className="space-y-6">
                
                {categoriesLoading || categories === undefined ? (
                  <div className="flex items-center justify-center py-8">
                    <MoonLoader size="lg" color="teal" />
                  </div>
                ) : filteredCategories.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                      <FolderTree className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2"><TranslatedText text="No expense categories found" /></h3>
                    <p className="text-slate-600 mb-6 max-w-md">
                      <TranslatedText text="Create your first expense category to start organizing your business expenses efficiently." />
                    </p>
                    <Button 
                      onClick={() => {
                        categoryForm.reset({
                          companyId: getCompanyId(),
                          name: '',
                          description: '',
                          parentId: '',
                          color: '#6b7280',
                          icon: '',
                          taxTreatment: 'deductible',
                          approvalThreshold: undefined
                        })
                        setIsCreateCategoryOpen(true)
                      }}
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      <TranslatedText text="Create First Category" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {renderCategoryTree(filteredCategories)}
                  </div>
                )}
                </div>
              </div>
            )}

            {activeTab === 'budgets' && (
              <div className="px-6 py-6">
                <div className="space-y-6">
                <div className="flex items-center gap-4 flex-wrap">
                  <Input placeholder={searchBudgetsPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="max-w-xs" />
                  <Select value={expenseStatus} onValueChange={setExpenseStatus}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder={tSync("Filter period")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all"><TranslatedText text="All Periods" /></SelectItem>
                      <SelectItem value="monthly"><TranslatedText text="Monthly" /></SelectItem>
                      <SelectItem value="quarterly"><TranslatedText text="Quarterly" /></SelectItem>
                      <SelectItem value="yearly"><TranslatedText text="Yearly" /></SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={expenseCategoryId || 'all-categories'} onValueChange={(v) => setExpenseCategoryId(v === 'all-categories' ? '' : v)}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder={tSync("Filter category")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      <SelectItem value="all-categories"><TranslatedText text="All Categories" /></SelectItem>
                      {filteredCategories.map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {budgetsLoading || budgets === undefined ? (
                  <div className="flex items-center justify-center py-8">
                    <MoonLoader size="lg" color="teal" />
                  </div>
                ) : filteredBudgets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                      <Target className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2"><TranslatedText text="No budgets created yet" /></h3>
                    <p className="text-slate-600 mb-6 max-w-md">
                      <TranslatedText text="Set up your first budget to track and control your expense spending across different categories." />
                    </p>
                    <Button 
                      onClick={() => {
                        budgetForm.reset({
                          companyId: getCompanyId(), // ✅ Use getCompanyId() instead of companies array
                          categoryId: '',
                          name: '',
                          description: '',
                          period: 'monthly',
                          startDate: '',
                          endDate: '',
                          amount: 0,
                          alertThreshold: 80
                        })
                        setIsCreateBudgetOpen(true)
                      }}
                      className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      <TranslatedText text="Create First Budget" />
                    </Button>
                  </div>
                ) : (
                  <Table>
                     <TableHeader>
                       <TableRow>
                         <TableHead className="px-4"><TranslatedText text="Budget Name" /></TableHead>
                         <TableHead className="px-4"><TranslatedText text="Amount" /></TableHead>
                         <TableHead className="px-4"><TranslatedText text="Spent" /></TableHead>
                         <TableHead className="px-4"><TranslatedText text="Status" /></TableHead>
                         <TableHead className="px-4"><TranslatedText text="Actions" /></TableHead>
                       </TableRow>
                     </TableHeader>
                    <TableBody>
                      {filteredBudgets.map((budget: Budget) => {
                        const status = getBudgetStatus(budget)
                        const utilization = getBudgetUtilization(budget)
                        
                        return (
                          <TableRow key={budget.id} className="hover:bg-slate-50">
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-3">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                {budget.name}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-lg font-semibold text-slate-900">
                              {formatCurrency(budget.amount)}
                              </div>
                              <div className="text-sm text-slate-500"><TranslatedText text="Total Budget" /></div>
                            </TableCell>
                            <TableCell>
                              <div className="text-lg font-semibold text-slate-900">
                              {formatCurrency(budget.spentAmount)}
                              </div>
                              <div className="text-sm text-slate-500"><TranslatedText text="Spent" /></div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-2">
                                <Badge className={`${getBudgetStatusColor(status)} px-3 py-1`}>
                                {status === 'over-budget' && <AlertTriangle className="w-3 h-3 mr-1" />}
                                {status === 'near-limit' && <AlertTriangle className="w-3 h-3 mr-1" />}
                                {status === 'within-budget' && <CheckCircle className="w-3 h-3 mr-1" />}
                                {status.replace('-', ' ')}
                              </Badge>
                                <div className="w-full bg-slate-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                      status === 'over-budget' ? 'bg-red-500' :
                                      status === 'near-limit' ? 'bg-yellow-500' :
                                      'bg-green-500'
                                    }`}
                                    style={{ width: `${Math.min(utilization * 100, 100)}%` }}
                                  ></div>
                                </div>
                                <div className="text-xs text-slate-500">
                                  {utilization.toFixed(1)}% utilized
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  aria-label={`View budget ${budget.name}`}
                                  onClick={() => {
                                    setSelectedBudget(budget)
                                    setIsViewBudgetOpen(true)
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  aria-label={`Edit budget ${budget.name}`}
                                  onClick={() => {
                                    setEditingBudget(budget)
                                    budgetForm.reset({
                                      companyId: (budget as any).companyId || getCompanyId(), // ✅ Use getCompanyId() as fallback
                                      categoryId: budget.category?.id || '',
                                      name: budget.name,
                                      description: budget.description || '',
                                      period: budget.period,
                                      startDate: budget.startDate ? new Date(budget.startDate).toISOString().split('T')[0] : '',
                                      endDate: budget.endDate ? new Date(budget.endDate).toISOString().split('T')[0] : '',
                                      amount: budget.amount,
                                      alertThreshold: budget.alertThreshold || 80
                                    })
                                    setIsCreateBudgetOpen(true)
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                )}
                </div>
              </div>
            )}

            {activeTab === 'rules' && (
              <div className="px-6 py-6">
                <div className="space-y-6">
                <div className="flex items-center gap-4 flex-wrap">
                  <Input placeholder={searchRulesPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="max-w-xs" />
                  <Select value={expenseStatus} onValueChange={setExpenseStatus}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder={tSync("Filter type")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all"><TranslatedText text="All Types" /></SelectItem>
                      <SelectItem value="amount_limit"><TranslatedText text="Amount Limit" /></SelectItem>
                      <SelectItem value="vendor_restriction"><TranslatedText text="Vendor Restriction" /></SelectItem>
                      <SelectItem value="approval_required"><TranslatedText text="Approval Required" /></SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={expenseCategoryId || 'all-categories'} onValueChange={(v) => setExpenseCategoryId(v === 'all-categories' ? '' : v)}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder={tSync("Filter category")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      <SelectItem value="all-categories"><TranslatedText text="All Categories" /></SelectItem>
                      {filteredCategories.map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {rulesLoading || rules === undefined ? (
                  <div className="flex items-center justify-center py-8">
                    <MoonLoader size="lg" color="teal" />
                  </div>
                ) : filteredRules.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                      <Settings className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2"><TranslatedText text="No expense rules configured" /></h3>
                    <p className="text-slate-600 mb-6 max-w-md">
                      <TranslatedText text="Create expense rules to automate approval workflows and enforce spending policies across your organization." />
                    </p>
                    <div className="flex gap-3">
                      <Button 
                        onClick={() => {
                          ruleForm.reset({
                            companyId: getCompanyId(),
                            categoryId: '',
                            name: 'Require Approval',
                            description: 'All expenses require approval before payment',
                            ruleType: 'approval_required',
                            amountLimit: undefined,
                            blockedVendors: '',
                            requireApproval: true,
                            notifyManager: false,
                            autoReject: false,
                            conditions: JSON.stringify({}, null, 2),
                            actions: JSON.stringify({ require_approval: true }, null, 2),
                            priority: 1,
                          })
                          setIsCreateRuleOpen(true)
                        }}
                        className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        <TranslatedText text="Create Approval Rule" />
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          ruleForm.reset({
                            companyId: getCompanyId(),
                            categoryId: '',
                            name: 'Amount Limit Rule',
                            description: 'Set spending limits for expense categories',
                            ruleType: 'amount_limit',
                            amountLimit: 1000,
                            blockedVendors: '',
                            requireApproval: false,
                            notifyManager: true,
                            autoReject: false,
                            conditions: '',
                            actions: '',
                            priority: 1,
                          })
                          setIsCreateRuleOpen(true)
                        }}
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        <TranslatedText text="Create Limit Rule" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Table>
                     <TableHeader>
                       <TableRow>
                         <TableHead className="px-4"><TranslatedText text="Rule Name" /></TableHead>
                         <TableHead className="px-4"><TranslatedText text="Type" /></TableHead>
                         <TableHead className="px-4"><TranslatedText text="Status" /></TableHead>
                         <TableHead className="px-4"><TranslatedText text="Actions" /></TableHead>
                       </TableRow>
                     </TableHeader>
                    <TableBody>
                      {filteredRules.map((rule: ExpenseRule) => (
                        <TableRow key={rule.id}>
                          <TableCell className="font-medium">{rule.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{rule.ruleType.replace('_', ' ')}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={rule.isActive ? 'default' : 'secondary'}>
                              {rule.isActive ? <TranslatedText text="Active" /> : <TranslatedText text="Inactive" />}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button variant="outline" size="sm" aria-label={`Toggle rule ${rule.name}`} onClick={() => updateRule.mutate({ id: rule.id, data: { isActive: !rule.isActive } })}>
                                {rule.isActive ? <TranslatedText text="Disable" /> : <TranslatedText text="Enable" />}
                              </Button>
                              <Button variant="outline" size="sm" aria-label={`Edit rule ${rule.name}`} onClick={() => {
                                setEditingRule(rule)
                                setIsCreateRuleOpen(true)
                                try {
                                  // Parse existing JSON data
                                  let conditions = {}
                                  let actions = {}
                                  
                                  try {
                                    conditions = typeof (rule as any).conditions === 'string' 
                                      ? JSON.parse((rule as any).conditions) 
                                      : (rule as any).conditions || {}
                                  } catch {}
                                  
                                  try {
                                    actions = typeof (rule as any).actions === 'string' 
                                      ? JSON.parse((rule as any).actions) 
                                      : (rule as any).actions || {}
                                  } catch {}
                                  
                                  // Extract user-friendly values
                                  const amountLimit = (conditions as any).amount || (conditions as any).limit
                                  const blockedVendors = Array.isArray((conditions as any).vendors) 
                                    ? (conditions as any).vendors.join(', ') 
                                    : ''
                                  
                                  ruleForm.reset({
                                    companyId: getCompanyId(),
                                    categoryId: rule.category?.id || '',
                                    name: rule.name,
                                    description: rule.description || '',
                                    ruleType: rule.ruleType as any,
                                    amountLimit: amountLimit,
                                    blockedVendors: blockedVendors,
                                    requireApproval: (actions as any).require_approval || false,
                                    notifyManager: (actions as any).notify_manager || false,
                                    autoReject: (actions as any).auto_reject || false,
                                    conditions: typeof (rule as any).conditions === 'string' ? (rule as any).conditions : JSON.stringify((rule as any).conditions || {}, null, 2),
                                    actions: typeof (rule as any).actions === 'string' ? (rule as any).actions : JSON.stringify((rule as any).actions || {}, null, 2),
                                    priority: rule.priority,
                                  })
                                } catch {}
                              }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" aria-label={`Delete rule ${rule.name}`} onClick={() => deleteRule.mutate(rule.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                </div>
              </div>
            )}

            {activeTab === 'expenses' && (
              <div className="px-6 py-6">
                <div className="space-y-6">
                <div className="flex items-center gap-3 flex-nowrap overflow-x-auto">
                  <Input 
                    placeholder={searchExpensesPlaceholder} 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    className="min-w-[200px] border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" 
                  />
                  <Select value={expenseStatus} onValueChange={setExpenseStatus}>
                    <SelectTrigger className="w-40 border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100">
                      <SelectValue placeholder={tSync("Filter status")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all"><TranslatedText text="All" /></SelectItem>
                      <SelectItem value="draft"><TranslatedText text="Draft" /></SelectItem>
                      <SelectItem value="submitted"><TranslatedText text="Submitted" /></SelectItem>
                      <SelectItem value="approved"><TranslatedText text="Approved" /></SelectItem>
                      <SelectItem value="paid"><TranslatedText text="Paid" /></SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    type="date" 
                    value={expenseStartDate} 
                    onChange={(e) => setExpenseStartDate(e.target.value)} 
                    className="w-40 border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" 
                  />
                  <Input 
                    type="date" 
                    value={expenseEndDate} 
                    onChange={(e) => setExpenseEndDate(e.target.value)} 
                    className="w-40 border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" 
                  />
                  <div className="relative flex-shrink-0 flex items-center gap-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleOcrFileChange}
                      className="hidden"
                      id="ocr-receipt-upload-main"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => document.getElementById('ocr-receipt-upload-main')?.click()}
                      disabled={ocrExtracting}
                      className="flex-shrink-0 border border-slate-200 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
                    >
                      {ocrExtracting ? (
                        <>
                          <MoonLoader size="sm" className="mr-2" color="teal" />
                          <TranslatedText text="Processing..." />
                        </>
                      ) : (
                        <>
                          <Receipt className="w-4 h-4 mr-2" />
                          <TranslatedText text="Scan Receipt" />
                        </>
                      )}
                    </Button>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => setCardImportOpen(true)}
                    className="flex-shrink-0 border border-slate-200 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
                  >
                    <TranslatedText text="Import Card CSV" />
                  </Button>
                </div>

                {expensesLoading || expenses === undefined ? (
                  <div className="flex items-center justify-center py-8">
                    <MoonLoader size="lg" color="teal" />
                  </div>
                ) : (!expenses || expenses.length === 0) ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                      <DollarSign className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2"><TranslatedText text="No expenses recorded" /></h3>
                    <p className="text-slate-600 mb-6 max-w-md">
                      <TranslatedText text="Start tracking your business expenses by creating your first expense entry or importing from receipts." />
                    </p>
                    <div className="flex gap-3">
                      <Button 
                        onClick={() => {
                          expenseForm.reset({
                            companyId: getCompanyId(),
                            expenseDate: new Date().toISOString().split('T')[0],
                            referenceNumber: '',
                            currency: 'Frw',
                            totalAmount: 0,
                            taxAmount: 0,
                            lineItems: []
                          })
                          setIsCreateExpenseOpen(true)
                        }}
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        <TranslatedText text="Create First Expense" />
                      </Button>
                      <div className="relative flex items-center gap-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleOcrFileChange}
                          className="hidden"
                          id="ocr-receipt-upload-empty"
                        />
                      <Button 
                        variant="outline"
                          onClick={() => document.getElementById('ocr-receipt-upload-empty')?.click()}
                          disabled={ocrExtracting}
                          className="hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
                        >
                          {ocrExtracting ? (
                            <>
                              <MoonLoader size="sm" className="mr-2" color="teal" />
                              <TranslatedText text="Processing..." />
                            </>
                          ) : (
                            <>
                              <Receipt className="w-4 h-4 mr-2" />
                        <TranslatedText text="Scan Receipt" />
                            </>
                          )}
                      </Button>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={() => setCardImportOpen(true)}
                        className="hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        <TranslatedText text="Import CSV" />
                      </Button>
                    </div>
                  </div>
                ) : (
                <>
                {/* Enhanced Sortable Expense Table */}
                {(() => {
                  // Filter expenses
                  let filteredExpenses = ((Array.isArray(expenses) ? expenses : expenses?.items || expenses?.data || []) as any[])
                    .filter((e) => !searchTerm || (e.description || '').toLowerCase().includes(searchTerm.toLowerCase()) || (e.vendorName || '').toLowerCase().includes(searchTerm.toLowerCase()) || (e.referenceNumber || '').toLowerCase().includes(searchTerm.toLowerCase()))
                    .filter((e) => !expenseStartDate || String(e.expenseDate || '').slice(0,10) >= expenseStartDate)
                    .filter((e) => !expenseEndDate || String(e.expenseDate || '').slice(0,10) <= expenseEndDate)
                  
                  // Sort expenses
                  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
                    let aVal: any = sortField === 'expenseDate' ? a.expenseDate : 
                                     sortField === 'amount' ? Number(a.totalAmount ?? a.amount ?? 0) :
                                     sortField === 'description' ? (a.description || '') :
                                     sortField === 'category' ? (a.category?.name || '') :
                                     sortField === 'vendor' ? (a.vendorName || '') :
                                     sortField === 'status' ? (a.status || '') :
                                     a[sortField]
                    let bVal: any = sortField === 'expenseDate' ? b.expenseDate : 
                                     sortField === 'amount' ? Number(b.totalAmount ?? b.amount ?? 0) :
                                     sortField === 'description' ? (b.description || '') :
                                     sortField === 'category' ? (b.category?.name || '') :
                                     sortField === 'vendor' ? (b.vendorName || '') :
                                     sortField === 'status' ? (b.status || '') :
                                     b[sortField]
                    
                    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
                    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
                    return 0
                  })
                  
                  // Paginate
                  const totalItems = sortedExpenses.length
                  const totalPages = Math.ceil(totalItems / itemsPerPage)
                  const startIndex = (currentPage - 1) * itemsPerPage
                  const paginatedExpenses = sortedExpenses.slice(startIndex, startIndex + itemsPerPage)
                  
                  const SortHeader = ({ field, label }: { field: string, label: string }) => (
                    <TableHead className="px-2 cursor-pointer select-none hover:bg-slate-100 text-sm" onClick={() => {
                      if (sortField === field) {
                        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                      } else {
                        setSortField(field)
                        setSortDirection('asc')
                      }
                    }}>
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm">{label}</span>
                        {sortField === field ? (
                          sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-green-600" /> : <ArrowDown className="w-3.5 h-3.5 text-green-600" />
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
                        )}
                      </div>
                    </TableHead>
                  )
                  
                  return (
                    <div className="space-y-4">
                      {/* Bulk Actions Toolbar */}
                      {selectedExpenses.size > 0 && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4 shadow-md">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Badge className="bg-green-600 text-white px-3 py-1 text-base font-semibold">
                                {selectedExpenses.size} {selectedExpenses.size === 1 ? <TranslatedText text="Expense" /> : <TranslatedText text="Expenses" />} <TranslatedText text="Selected" />
                              </Badge>
                              <Separator orientation="vertical" className="h-6 bg-green-300" />
                              <span className="text-sm text-slate-600 font-medium">
                                <TranslatedText text="Total" />: {convertingSelectedTotal ? (
                                  <span className="text-slate-400">Converting...</span>
                                ) : selectedExpensesTotalRWF !== null ? (
                                  `${selectedExpensesTotalRWF.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RWF`
                                ) : (
                                  `${sortedExpenses.filter(e => selectedExpenses.has(e.id)).reduce((sum, e) => sum + Number(e.totalAmount || e.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RWF`
                                )}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white border-green-700"
                                onClick={handleBulkApprove}
                                disabled={bulkApproving}
                              >
                                {bulkApproving ? (
                                  <>
                                    <MoonLoader size="sm" className="mr-2" color="teal" />
                                    <TranslatedText text="Approving..." />
                                  </>
                                ) : (
                                  <>
                                <Check className="w-4 h-4 mr-2" />
                                <TranslatedText text="Approve Selected" />
                                  </>
                                )}
                              </Button>
                              <Separator orientation="vertical" className="h-6 bg-green-300" />
                              <Button
                                variant="outline"
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white border-red-700"
                                onClick={handleBulkDeleteClick}
                                disabled={bulkDeleting || deleteExpense.isPending}
                              >
                                {bulkDeleting || deleteExpense.isPending ? (
                                  <>
                                    <MoonLoader size="sm" className="mr-2" color="gray" />
                                    <TranslatedText text="Deleting..." />
                                  </>
                                ) : (
                                  <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    <TranslatedText text="Delete Selected" />
                                  </>
                                )}
                              </Button>
                              <Separator orientation="vertical" className="h-6 bg-green-300" />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedExpenses(new Set())}
                              >
                                <X className="w-4 h-4 mr-2" />
                                <TranslatedText text="Clear Selection" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Table Controls */}
                      <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-700"><TranslatedText text="Show" /></span>
                            <Select value={String(itemsPerPage)} onValueChange={(val) => {
                              setItemsPerPage(Number(val))
                              setCurrentPage(1)
                            }}>
                              <SelectTrigger className="w-20 h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="5">5</SelectItem>
                                <SelectItem value="10">10</SelectItem>
                                <SelectItem value="25">25</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                                <SelectItem value="100">100</SelectItem>
                              </SelectContent>
                            </Select>
                            <span className="text-sm text-slate-600">
                              {totalItems === 0 ? <TranslatedText text="No expenses" /> : <><TranslatedText text="Showing" /> {startIndex + 1}-{Math.min(startIndex + itemsPerPage, totalItems)} <TranslatedText text="of" /> {totalItems} <TranslatedText text="expenses" /></>}
                            </span>
                          </div>
                          {selectedExpenses.size > 0 && (
                            <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                              {selectedExpenses.size} <TranslatedText text="selected" />
                            </Badge>
                          )}
                          {expenseStatus === 'draft' || expenseStatus === 'all' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => {
                                const expensesList = (Array.isArray(expenses) ? expenses : expenses?.items || expenses?.data || []) as any[]
                                const draftExpenses = expensesList.filter((e: any) => e.status === 'draft')
                                setSelectedExpenses(new Set(draftExpenses.map((e: any) => e.id)))
                                toast.success(`Selected all ${draftExpenses.length} draft expenses`)
                              }}
                            >
                              <Check className="w-3 h-3 mr-1" />
                              <TranslatedText text="Select All Draft" />
                            </Button>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              queryClient.invalidateQueries({ queryKey: ['expenses'] })
                              toast.success('Expenses refreshed')
                            }}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh
                          </Button>
                          <Select 
                            value=""
                            onValueChange={(value) => {
                              const expensesToExport = selectedExpenses.size > 0
                                ? sortedExpenses.filter(e => selectedExpenses.has(e.id))
                                : sortedExpenses
                              
                              if (value === 'csv') exportToCSV(expensesToExport)
                              else if (value === 'excel') exportToExcel(expensesToExport)
                              else if (value === 'print') printExpenses(expensesToExport)
                            }}
                          >
                            <SelectTrigger className="w-[140px] h-9">
                              <div className="flex items-center gap-2">
                                <Download className="w-4 h-4" />
                                <span>Export</span>
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="csv">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4" />
                                  <span>Export as CSV</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="excel">
                                <div className="flex items-center gap-2">
                                  <FileSpreadsheet className="w-4 h-4" />
                                  <span>Export as Excel</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="print">
                                <div className="flex items-center gap-2">
                                  <Printer className="w-4 h-4" />
                                  <span>Print Report</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Enhanced Table */}
                      <div className="border border-slate-200 rounded-lg overflow-x-auto">
                        <Table className="min-w-full">
                          <TableHeader className="bg-slate-50">
                            <TableRow className="h-10">
                              <TableHead className="px-2 w-10">
                                <input 
                                  type="checkbox" 
                                  className="w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
                                  checked={paginatedExpenses.length > 0 && paginatedExpenses.every(e => selectedExpenses.has(e.id))}
                                  onChange={(event) => {
                                    if (event.target.checked) {
                                      setSelectedExpenses(new Set([...selectedExpenses, ...paginatedExpenses.map(e => e.id)]))
                                    } else {
                                      const newSet = new Set(selectedExpenses)
                                      paginatedExpenses.forEach(e => newSet.delete(e.id))
                                      setSelectedExpenses(newSet)
                                    }
                                  }}
                                />
                              </TableHead>
                              <SortHeader field="expenseDate" label="Date" />
                              <SortHeader field="category" label="Category" />
                              <TableHead className="px-2 cursor-pointer select-none hover:bg-slate-100 min-w-[180px]" onClick={() => {
                                if (sortField === 'amount') {
                                  setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                                } else {
                                  setSortField('amount')
                                  setSortDirection('asc')
                                }
                              }}>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-semibold">Amount & Tax</span>
                                  {sortField === 'amount' ? (
                                    sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-green-600" /> : <ArrowDown className="w-3.5 h-3.5 text-green-600" />
                                  ) : (
                                    <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
                                  )}
                                </div>
                              </TableHead>
                              <SortHeader field="status" label="Status" />
                              <TableHead className="px-2 w-24"><span className="text-sm font-semibold">Actions</span></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {paginatedExpenses.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={6} className="text-center py-8">
                                  <div className="flex flex-col items-center gap-2 text-slate-500">
                                    <FileText className="w-10 h-10 text-slate-300" />
                                    <p className="text-sm font-medium">No expenses found</p>
                                    <p className="text-xs">Try adjusting your filters or create a new expense</p>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ) : (
                              paginatedExpenses.map((e) => (
                                <TableRow 
                                  key={e.id} 
                                  className={`hover:bg-slate-50 transition-colors ${selectedExpenses.has(e.id) ? 'bg-green-50' : ''}`}
                                >
                                  <TableCell className="px-2 py-2">
                                    <input 
                                      type="checkbox" 
                                      className="w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
                                      checked={selectedExpenses.has(e.id)}
                                      onChange={(event) => {
                                        const newSet = new Set(selectedExpenses)
                                        if (event.target.checked) {
                                          newSet.add(e.id)
                                        } else {
                                          newSet.delete(e.id)
                                        }
                                        setSelectedExpenses(newSet)
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell className="px-2 py-2">
                                    <div className="flex items-center gap-2">
                                      <div className="w-1.5 h-1.5 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full"></div>
                                      <div className="text-sm font-medium text-slate-900 whitespace-nowrap">
                                          {e.expenseDate ? format(new Date(e.expenseDate), 'MMM dd, yyyy') : '-'}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="px-2 py-2">
                                    <Badge variant="outline" className="px-2 py-0.5 text-xs font-medium">
                                      {e.category?.name || 'Uncategorized'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="px-2 py-2 min-w-[180px]">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-bold text-slate-900 whitespace-nowrap">
                                        {Number(e.totalAmount ?? e.amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {e.currency || 'Frw'}
                                      </span>
                                      {Number((e as any).taxAmount || 0) > 0 && (
                                        <Badge variant="outline" className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 border-green-200 shrink-0 whitespace-nowrap">
                                          Tax: {Number((e as any).taxAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {e.currency || 'Frw'}
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="px-2 py-2">
                                    <div className="flex items-center gap-1.5">
                                      <Badge className={`px-2 py-0.5 text-xs flex items-center gap-1 w-fit ${
                                      e.status === 'approved' ? 'bg-green-100 text-green-800 border-green-200' :
                                      e.status === 'pending' || e.status === 'submitted' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                                      e.status === 'rejected' ? 'bg-red-100 text-red-800 border-red-200' :
                                      'bg-gray-100 text-gray-800 border-gray-200'
                                    }`}>
                                      {e.status === 'approved' && <CheckCircle className="w-3 h-3" />}
                                      {(e.status === 'pending' || e.status === 'submitted') && <AlertTriangle className="w-3 h-3" />}
                                      {e.status === 'rejected' && <AlertTriangle className="w-3 h-3" />}
                                        <span className="capitalize font-medium text-xs">{e.status || 'draft'}</span>
                                    </Badge>
                                    {e.status !== 'draft' && (
                                      <ExpenseJournalIndicator expenseId={e.id} />
                                    )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="px-2 py-2">
                                    <div className="flex items-center gap-1">
                                      {e.status === 'draft' && (
                                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200" disabled={submittingExpenseId === e.id} onClick={() => handleSubmitExpense(e)}>
                                          {submittingExpenseId === e.id ? (
                                            <>
                                              <MoonLoader size="sm" className="mr-1.5" color="blue" />
                                              <span className="text-xs">Submitting...</span>
                                            </>
                                          ) : (
                                            <span className="text-xs">Submit</span>
                                          )}
                                        </Button>
                                      )}
                                      {e.status === 'submitted' && (
                                        <>
                                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-green-50 hover:bg-green-100 text-green-700 border-green-200" disabled={approvingExpenseId === e.id} onClick={() => handleApproveExpense(e)}>
                                            {approvingExpenseId === e.id ? (
                                              <>
                                                <MoonLoader size="sm" className="mr-1.5" color="green" />
                                                <span className="text-xs">Approving...</span>
                                              </>
                                            ) : (
                                              <span className="text-xs">Approve</span>
                                            )}
                                          </Button>
                                          {/* <Button size="sm" variant="outline" onClick={() => { setMatchingContext({ amount: Number(e.totalAmount ?? e.amount ?? 0), date: e.expenseDate?.slice(0,10), description: e.description }); setMatchingOpen(true) }}>
                                            Match
                                          </Button> */}
                                        </>
                                      )}
                                      {/* {e.status === 'approved' && (
                                        <Button size="sm" variant="outline" className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200" onClick={() => { setSelectedExpenseId(e.id); setReimburseOpen(true) }}>
                                          Reimburse
                                        </Button>
                                      )} */}
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setSelectedExpense(e); setIsViewExpenseOpen(true) }}>
                                        <Eye className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingExpense(e); setEditOpen(true) }}>
                                        <Edit className="w-3.5 h-3.5" />
                                      </Button>
                                      {e.status?.toLowerCase() === 'draft' && (
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" 
                                          onClick={() => {
                                            setExpenseToDelete(e)
                                            setDeleteConfirmOpen(true)
                                          }}
                                          disabled={deleteExpense.isPending}
                                          title="Delete expense"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                      )}
                                      {/* {e.status !== 'draft' && (
                                        <Button 
                                          size="sm" 
                                          variant="outline" 
                                          className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                                          disabled={generateJournalEntry.isPending}
                                          onClick={async () => {
                                            const hasEntries = await hasJournalEntries(e.id)
                                            if (hasEntries) {
                                              toast.error('Journal entries already exist for this expense')
                                              return
                                            }
                                            generateJournalEntry.mutate({ expenseId: e.id, silent: false })
                                          }}
                                          title="Generate Journal Entry"
                                        >
                                          {generateJournalEntry.isPending ? (
                                            <MoonLoader size="sm" color="blue" />
                                          ) : (
                                            <BookOpen className="w-4 h-4" />
                                          )}
                                        </Button>
                                      )} */}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg border border-slate-200">
                          <div className="text-sm text-slate-600">
                            Page {currentPage} of {totalPages}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(1)}
                            >
                              First
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(currentPage - 1)}
                            >
                              <ChevronLeft className="w-4 h-4 mr-1" />
                              Previous
                            </Button>
                            <div className="flex items-center gap-1">
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum
                                if (totalPages <= 5) {
                                  pageNum = i + 1
                                } else if (currentPage <= 3) {
                                  pageNum = i + 1
                                } else if (currentPage >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i
                                } else {
                                  pageNum = currentPage - 2 + i
                                }
                                return (
                                  <Button
                                    key={pageNum}
                                    variant={currentPage === pageNum ? 'default' : 'outline'}
                                    size="sm"
                                    className={currentPage === pageNum ? 'bg-green-600 hover:bg-green-700' : ''}
                                    onClick={() => setCurrentPage(pageNum)}
                                  >
                                    {pageNum}
                                  </Button>
                                )
                              })}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={currentPage === totalPages}
                              onClick={() => setCurrentPage(currentPage + 1)}
                            >
                              Next
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={currentPage === totalPages}
                              onClick={() => setCurrentPage(totalPages)}
                            >
                              Last
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })()}
                </>
                )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Modal Components */}
      <>
      <ReceiptCaptureModal open={receiptOpen} onOpenChange={setReceiptOpen} onCreated={() => queryClient.invalidateQueries({ queryKey: ['expenses'] })} />
      <ExpenseReportModal open={reportOpen} onOpenChange={setReportOpen} />
      <ReimburseExpenseModal open={reimburseOpen} onOpenChange={(v) => { setReimburseOpen(v); if (!v) { setSelectedExpenseId(null); queryClient.invalidateQueries({ queryKey: ['expenses'] }) } }} expenseId={selectedExpenseId} />
      <ExpenseMatchingModal open={matchingOpen} onOpenChange={setMatchingOpen} amount={matchingContext?.amount} date={matchingContext?.date} description={matchingContext?.description} />
      <EditExpenseModal open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) { setEditingExpense(null); queryClient.invalidateQueries({ queryKey: ['expenses'] }) } }} expense={editingExpense} />
      <CardCsvImportModal open={cardImportOpen} onOpenChange={(v) => { setCardImportOpen(v); if (!v) { queryClient.invalidateQueries({ queryKey: ['expenses'] }) } }} />
      
      {/* Single Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={(open) => {
        setDeleteConfirmOpen(open)
        if (!open) {
          setExpenseToDelete(null)
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-slate-900">Delete Expense</DialogTitle>
                <DialogDescription className="text-sm text-slate-600 mt-1">
                  Are you sure you want to delete this expense? This action cannot be undone.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {expenseToDelete && (
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4">
              <div className="space-y-2">
                <p className="font-medium text-red-900">{expenseToDelete.description || 'Expense'}</p>
                <p className="text-sm text-red-700">
                  Amount: {formatCurrency(expenseToDelete.totalAmount || expenseToDelete.amount || 0, expenseToDelete.currency || 'Frw')}
                </p>
                {expenseToDelete.referenceNumber && (
                  <p className="text-sm text-red-700">Reference: {expenseToDelete.referenceNumber}</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false)
                setExpenseToDelete(null)
              }}
              className="h-9"
              disabled={deleteExpense.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSingleDeleteConfirm}
              disabled={deleteExpense.isPending}
              className="h-9"
            >
              {deleteExpense.isPending ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="gray" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Expense
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={bulkDeleteConfirmOpen} onOpenChange={(open) => {
        setBulkDeleteConfirmOpen(open)
        if (!open) {
          // Don't clear selection on cancel, let user keep it
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-slate-900">Delete Expenses</DialogTitle>
                <DialogDescription className="text-sm text-slate-600 mt-1">
                  Are you sure you want to delete the selected draft expense(s)? This action cannot be undone.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {(() => {
            const allExpenses = Array.isArray(expenses) ? expenses : expenses?.items || expenses?.data || []
            const selectedExpenseIds = Array.from(selectedExpenses)
            const draftExpenses = allExpenses.filter((e: any) => 
              selectedExpenseIds.includes(e.id) && (e.status || 'draft').toLowerCase() === 'draft'
            )
            return (
              <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4">
                <div className="space-y-2">
                  <p className="font-medium text-red-900">
                    {draftExpenses.length} draft expense(s) will be deleted
                  </p>
                  <p className="text-sm text-red-700">
                    Total amount: {formatCurrency(
                      draftExpenses.reduce((sum, e) => sum + Number(e.totalAmount || e.amount || 0), 0),
                      'Frw'
                    )}
                  </p>
                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mt-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium">Warning:</p>
                        <p className="mt-1">This action cannot be undone. Only draft expenses will be deleted.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })()}
          
          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => setBulkDeleteConfirmOpen(false)}
              className="h-9"
              disabled={bulkDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteConfirm}
              disabled={bulkDeleting}
              className="h-9"
            >
              {bulkDeleting ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="gray" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* OCR Results Modal */}
      <Dialog open={ocrResultsOpen} onOpenChange={setOcrResultsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-teal-600" />
              OCR Extraction Results
            </DialogTitle>
            <DialogDescription>
              Review the extracted data from the receipt. You can proceed to create the expense or edit the data manually.
            </DialogDescription>
          </DialogHeader>
          
          {ocrExtractedData && (
            <div className="space-y-4">
              {/* Receipt Preview */}
              {ocrPreviewUrl && (
                <div className="border rounded-lg p-2 bg-slate-50">
                  <img src={ocrPreviewUrl} alt="Receipt preview" className="max-h-48 w-auto mx-auto object-contain rounded" />
                </div>
              )}
              
              {/* Extracted Data */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-500">Vendor/Supplier</label>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-sm font-medium text-slate-900">
                      {ocrExtractedData.vendor || <span className="text-slate-400 italic">Not extracted</span>}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-500">Amount</label>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-sm font-medium text-slate-900">
                      {ocrExtractedData.amount ? `${ocrExtractedData.amount.toFixed(2)} Frw` : <span className="text-slate-400 italic">Not extracted</span>}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-500">Date</label>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-sm font-medium text-slate-900">
                      {ocrExtractedData.date ? (() => {
                        const date = new Date(ocrExtractedData.date)
                        return isNaN(date.getTime()) ? (
                          <span className="text-slate-400 italic">{ocrExtractedData.date} (Invalid date)</span>
                        ) : (
                          format(date, 'MMM dd, yyyy')
                        )
                      })() : <span className="text-slate-400 italic">Not extracted</span>}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-500">Description</label>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-sm font-medium text-slate-900 line-clamp-2">
                      {ocrExtractedData.description || <span className="text-slate-400 italic">Not extracted</span>}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Items List (if items detected) */}
              {ocrExtractedData.items && ocrExtractedData.items.length > 0 && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-500">
                    Line Items Found: 1 expense with {ocrExtractedData.items.length} line item{ocrExtractedData.items.length !== 1 ? 's' : ''} will be created
                  </label>
                  <div className="p-3 bg-blue-50 rounded-md border border-blue-200 max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      {ocrExtractedData.items.map((item, index) => (
                        <div key={index} className="flex justify-between items-center text-sm py-1 border-b border-blue-100 last:border-0">
                          <span className="text-slate-900 font-medium">{item.name}</span>
                          <span className="text-slate-700">{item.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 italic">
                    All items will be created as line items in a single expense entry
                  </p>
                </div>
              )}
              
              {/* Raw OCR Text */}
              {ocrExtractedData.rawText && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-500">Raw OCR Text</label>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200 max-h-40 overflow-y-auto">
                    <p className="text-xs text-slate-600 whitespace-pre-wrap font-mono">
                      {ocrExtractedData.rawText
                        .replace(/\bDaze\b/gi, 'Date')
                        .replace(/\bTize\b/gi, 'Time')
                        .replace(/\bDaze:\s*/gi, 'Date: ')
                        .replace(/\bTize:\s*/gi, 'Time: ')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setOcrResultsOpen(false)} disabled={bulkApproving}>
              Cancel
            </Button>
            <Button 
              onClick={proceedWithExtractedData} 
              className="bg-teal-600 hover:bg-teal-700"
              disabled={bulkApproving}
            >
              {bulkApproving ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  Creating Expenses...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Proceed to Create Expense
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Create Expense Dialog */}
      <Dialog modal={false} open={isCreateExpenseOpen} onOpenChange={(open) => {
        setIsCreateExpenseOpen(open)
        if (!open) {
          expenseForm.reset()
          clearOcrData()
        }
      }}>
        <DialogContent className="!max-w-7xl max-h-[95vh] overflow-hidden w-[95vw] flex flex-col bg-white">
          <DialogHeader className="pb-3 border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center shadow-sm">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-teal-500 rounded-full border-2 border-white flex items-center justify-center">
                    <Plus className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>
                <div>
                  <DialogTitle className="text-xl font-bold text-slate-900">
                    Create New Expense
                  </DialogTitle>
                  <DialogDescription className="text-slate-600 mt-0.5 text-xs">
                    Professional expense tracking • Full audit trail • Real-time calculations
                  </DialogDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="bg-teal-100 text-teal-700 border-teal-200 px-3 py-1 text-xs font-semibold">
                Draft Mode
              </Badge>
            </div>
            </div>
          </DialogHeader>
          
          <Form {...expenseForm}>
            <form onSubmit={expenseForm.handleSubmit(onSubmitExpense)} className="flex flex-col" style={{ maxHeight: 'calc(95vh - 140px)' }}>
              <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400">
                <ExpenseFormFields
                  form={expenseForm}
                  companyName={companyName}
                  filteredCategories={filteredCategories}
                  filteredBudgets={filteredBudgets}
                  vendors={vendors || []}
                />
                
                <div className="border-t border-slate-200 bg-slate-50 pt-3 px-4 pb-3 mt-4">
                  <ExpenseSummary form={expenseForm} />
                </div>
              </div>
              
              <div className="flex-shrink-0 bg-white border-t border-slate-200 p-4 shadow-sm">
                <div className="flex items-center justify-between w-full">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsCreateExpenseOpen(false)}
                    disabled={createExpense.isPending}
                    className="h-10 px-6 text-sm font-semibold border border-slate-300 hover:bg-slate-100 transition-all duration-200"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={createExpense.isPending}
                    className="h-10 px-8 text-sm font-semibold bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    {createExpense.isPending ? (
                      <>
                        <MoonLoader size="sm" className="mr-2" color="teal" />
                        Creating Expense...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Create Expense
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      {/* View Expense Dialog */}
      <Dialog open={isViewExpenseOpen} onOpenChange={(open) => {
        setIsViewExpenseOpen(open)
        if (!open) {
          setSelectedExpense(null)
        }
      }}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Expense Details</DialogTitle>
            <DialogDescription>View complete information about this expense</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400 pr-2">
            {expenseDetailsLoading ? (
              <div className="flex items-center justify-center py-8">
                <MoonLoader size="lg" color="teal" />
              </div>
            ) : expenseDetails ? (
              <div className="space-y-6 pb-4">
              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Reference Number</label>
                  <p className="text-lg font-medium">{expenseDetails.referenceNumber || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Total Amount</label>
                  <p className="text-lg font-bold text-green-600">{Number(expenseDetails.totalAmount ?? expenseDetails.amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {expenseDetails.currency || 'Frw'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Date</label>
                  <p className="text-lg">{expenseDetails.expenseDate ? new Date(expenseDetails.expenseDate).toLocaleDateString() : '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    <Badge variant={expenseDetails.status === 'paid' ? 'default' : 'secondary'}>
                      {expenseDetails.status}
                    </Badge>
                  </div>
                </div>
                {expenseDetails.taxAmount && Number(expenseDetails.taxAmount) > 0 && (
                <div>
                    <label className="text-sm font-medium text-gray-500">Total Tax</label>
                    <p className="text-lg font-semibold text-teal-700">{Number(expenseDetails.taxAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {expenseDetails.currency || 'Frw'}</p>
                </div>
                )}
                </div>

              {/* Line Items */}
              {(expenseDetails.lineItems && Array.isArray(expenseDetails.lineItems) && expenseDetails.lineItems.length > 0) ? (
                <div className="space-y-4">
                  <Separator />
                  <h3 className="text-lg font-medium">Line Items</h3>
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold min-w-[100px]">Item Code</TableHead>
                          <TableHead className="font-semibold min-w-[200px]">Description</TableHead>
                          <TableHead className="font-semibold min-w-[150px]">Category</TableHead>
                          <TableHead className="font-semibold min-w-[150px]">Vendor</TableHead>
                          <TableHead className="font-semibold text-right min-w-[80px]">Qty</TableHead>
                          <TableHead className="font-semibold text-right min-w-[120px]">Unit Price</TableHead>
                          <TableHead className="font-semibold text-right min-w-[100px]">Tax Rate</TableHead>
                          <TableHead className="font-semibold text-right min-w-[130px]">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expenseDetails.lineItems.map((item: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell className="text-sm whitespace-nowrap">{item.itemCode || '-'}</TableCell>
                            <TableCell className="text-sm font-medium">{item.description || '-'}</TableCell>
                            <TableCell className="whitespace-nowrap">
                              <Badge variant="outline" className="text-xs">
                                {item.category?.name || 'Uncategorized'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">{item.vendorName || expenseDetails.vendorName || '-'}</TableCell>
                            <TableCell className="text-sm text-right whitespace-nowrap">{Number(item.quantity || 1).toLocaleString()}</TableCell>
                            <TableCell className="text-sm text-right whitespace-nowrap">{Number(item.unitPrice || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {expenseDetails.currency || 'Frw'}</TableCell>
                            <TableCell className="text-sm text-right whitespace-nowrap">{Number(item.taxRate || 0).toFixed(2)}%</TableCell>
                            <TableCell className="text-sm font-semibold text-right whitespace-nowrap">{Number(item.lineTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {expenseDetails.currency || 'Frw'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
              </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Separator />
                  <h3 className="text-lg font-medium">Description</h3>
                  <p className="text-lg">{expenseDetails.description || 'No description'}</p>
                </div>
              )}

              {/* Additional Information */}
              {(expenseDetails.notes || expenseDetails.department || expenseDetails.project) && (
                <div className="space-y-4">
                  <Separator />
                  <h3 className="text-lg font-medium">Additional Information</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {expenseDetails.notes && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Notes</label>
                        <p className="text-lg">{expenseDetails.notes}</p>
                      </div>
                    )}
                    {expenseDetails.department && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Department</label>
                        <p className="text-lg">{expenseDetails.department}</p>
                      </div>
                    )}
                    {expenseDetails.project && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Project</label>
                        <p className="text-lg">{expenseDetails.project}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Receipt */}
              {expenseDetails.receiptUrl && (
                <div className="space-y-4">
                  <Separator />
                  <h3 className="text-lg font-medium">Receipt</h3>
                  <div className="flex items-center space-x-4">
                    <Button variant="outline" onClick={() => window.open(expenseDetails.receiptUrl, '_blank')}>
                      View Receipt
                    </Button>
                  </div>
                </div>
              )}

              {/* Journal Entries */}
              <div className="space-y-4">
                <Separator />
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Journal Entries
                </h3>
                <ExpenseJournalEntries expenseId={expenseDetails.id} />
              </div>

              {/* Timestamps */}
              <div className="space-y-4">
                <Separator />
                <h3 className="text-lg font-medium">Timestamps</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created</label>
                    <p className="text-lg">{expenseDetails.createdAt ? new Date(expenseDetails.createdAt).toLocaleString() : '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Updated</label>
                    <p className="text-lg">{expenseDetails.updatedAt ? new Date(expenseDetails.updatedAt).toLocaleString() : '-'}</p>
                  </div>
                </div>
              </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No expense details found</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Vendor Modal */}
      <VendorModal
        isOpen={isVendorModalOpen}
        onClose={() => setIsVendorModalOpen(false)}
        onSubmit={async (vendorData) => {
          await createVendorMutation.mutateAsync(vendorData)
        }}
        companyId={firstCompanyId}
        isLoading={createVendorMutation.isPending}
      />
      </>
    </PageLayout>
  )
}
